/* prettier-ignore-file */
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  TextInput,
  Image,
  Modal,
} from "react-native";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db, storage } from "../firebase.config";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
  setDoc,
  deleteDoc,
  Timestamp,
  arrayUnion,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable, getFunctions } from "firebase/functions";
import AuthHeader from "../components/AuthHeader";
import { getCollectionPath, getSettingsPath } from "../utils/dbPaths";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import { useSettings, useProducts, useCheckInStore, useBookings, useAuthStore, useCustomer, useStripeTestMode, getSelectedUserId } from "../stores";
import { scheduleReviewRequest } from "../db_calls_wrapper";
import PinAuthLayer from "../components/PinAuthLayer";
import { isDeviceTrusted } from "../utils/deviceTrust";
import { RENTAL_APP_BASE_URL } from "../utils/appUrl";
import { createChangeTrackingEntry } from "../utils/helperFuns";
import {
  capitalizeName,
  centsToDollars,
  formatCentsAsUSD,
  formatDollarsAsUSD,
} from "../utils/formatting";
import { app } from "../firebase.config";

const functions = getFunctions(app);

const CheckIn = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  
  // Get data from Zustand store
  const booking = useCheckInStore((state) => state.booking);
  const bookingIntake = useCheckInStore((state) => state.bookingIntake);
  const products1Catalog = useCheckInStore((state) => state.products1Catalog);
  const products2Catalog = useCheckInStore((state) => state.products2Catalog);
  const shopInfo = useCheckInStore((state) => state.shopInfo);
  const storeLogo = useCheckInStore((state) => state.storeLogo);
  const product1CategoryName = useCheckInStore((state) => state.product1CategoryName);
  const product2CategoryName = useCheckInStore((state) => state.product2CategoryName);
  
  // Get setters from Zustand store
  const setBooking = useCheckInStore((state) => state.setBooking);
  const setBookingIntake = useCheckInStore((state) => state.setBookingIntake);
  const updateBookingIntake = useCheckInStore((state) => state.updateBookingIntake);
  const setProducts1Catalog = useCheckInStore((state) => state.setProducts1Catalog);
  const setProducts2Catalog = useCheckInStore((state) => state.setProducts2Catalog);
  const setShopInfo = useCheckInStore((state) => state.setShopInfo);
  const setStoreLogo = useCheckInStore((state) => state.setStoreLogo);
  const setProduct1CategoryName = useCheckInStore((state) => state.setProduct1CategoryName);
  const setProduct2CategoryName = useCheckInStore((state) => state.setProduct2CategoryName);
  
  // UI state
  const [showImages, setShowImages] = useState({}); // { productId: boolean }
  const [reasonModals, setReasonModals] = useState({}); // { productId: boolean }
  const [reasonTexts, setReasonTexts] = useState({}); // { productId: string }
  const [focusedInputs, setFocusedInputs] = useState({}); // { "product1_itemId": boolean, "product2_itemId": boolean }
  const [mediaViewerModals, setMediaViewerModals] = useState({}); // { "product1_itemId": boolean, "product2_itemId": boolean }
  const [textViewerModals, setTextViewerModals] = useState({}); // { "product1_itemId": boolean, "product2_itemId": boolean }
  const [selectedMedia, setSelectedMedia] = useState(null); // { type: "photo"|"video", url: string }
  const [chargeModalVisible, setChargeModalVisible] = useState(false);
  const [chargeProcessing, setChargeProcessing] = useState(false);
  const [chargeSuccess, setChargeSuccess] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [completeBookingModalVisible, setCompleteBookingModalVisible] = useState(false);
  const [completeBookingProcessing, setCompleteBookingProcessing] = useState(false);
  const [reviewAskVisible, setReviewAskVisible] = useState(false);
  const [reviewAskBusy, setReviewAskBusy] = useState(false);
  const [saveForReviewModalVisible, setSaveForReviewModalVisible] = useState(false);
  const [saveForReviewProcessing, setSaveForReviewProcessing] = useState(false);
  
  // Slider state for charge confirmation
  const [chargeSliderPosition, setChargeSliderPosition] = useState(0);
  const chargeSliderTrackRef = useRef(null);
  
  // Slider state for complete booking
  const [completeSliderPosition, setCompleteSliderPosition] = useState(0);
  const completeSliderTrackRef = useRef(null);

  // Load booking from Zustand store or completedBookings
  const bookingsFromStore = useBookings((state) => state.bookings);
  
  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId) return;
      
      // Reset initial load flag when booking changes
      isInitialLoadRef.current = true;
      lastSavedIntakeRef.current = null;

      // First check Zustand store (active bookings)
      const foundBooking = bookingsFromStore.find(b => b.id === bookingId || b.bookingId === bookingId);
      
      if (foundBooking) {
        console.log("✅ Booking found in active bookings store");
        setBooking(foundBooking);
        
        // Initialize bookingIntake
        if (foundBooking.bookingIntake) {
          setBookingIntake(foundBooking.bookingIntake);
            
            // Load reason texts
            const reasons = {};
          Object.keys(foundBooking.bookingIntake.product1 || {}).forEach(id => {
            const reason = foundBooking.bookingIntake.product1[id].penaltyReason || "";
            if (reason) {
              reasons[`product1_${id}`] = reason;
              }
            });
          Object.keys(foundBooking.bookingIntake.product2 || {}).forEach(id => {
            const reason = foundBooking.bookingIntake.product2[id].penaltyReason || "";
            if (reason) {
              reasons[`product2_${id}`] = reason;
              }
            });
            setReasonTexts(reasons);
        } else {
          // Initialize empty bookingIntake - products will be initialized after products load
          setBookingIntake({
            product1: {},
            product2: {},
          });
        }
        setLoading(false);
        return;
      }
      
      // Not in store - check if store is still loading
      if (bookingsFromStore.length === 0) {
        console.log("⏳ Bookings store is empty, waiting for data...");
        return; // Still loading
      }
      
      // Store is loaded but booking not found - check completedBookings
      console.log("🔍 Booking not in active bookings, checking completedBookings...");
      try {
        const completedPath = getCollectionPath("completedBookings");
        const bookingDoc = await getDoc(doc(db, completedPath, bookingId));
        
        if (bookingDoc.exists()) {
          console.log("✅ Booking found in completedBookings");
          const completedBooking = { id: bookingDoc.id, ...bookingDoc.data() };
          setBooking(completedBooking);
          
          // Initialize bookingIntake for completed booking
          if (completedBooking.bookingIntake) {
            setBookingIntake(completedBooking.bookingIntake);
            
            // Load reason texts
            const reasons = {};
            Object.keys(completedBooking.bookingIntake.product1 || {}).forEach(id => {
              const reason = completedBooking.bookingIntake.product1[id].penaltyReason || "";
              if (reason) {
                reasons[`product1_${id}`] = reason;
              }
            });
            Object.keys(completedBooking.bookingIntake.product2 || {}).forEach(id => {
              const reason = completedBooking.bookingIntake.product2[id].penaltyReason || "";
              if (reason) {
                reasons[`product2_${id}`] = reason;
              }
            });
            setReasonTexts(reasons);
          } else {
            setBookingIntake({
              product1: {},
              product2: {},
            });
          }
        } else {
          console.log("❌ Booking not found in completedBookings either");
        }
      } catch (err) {
        console.error("❌ Error checking completedBookings:", err);
      } finally {
        setLoading(false);
      }
    };
    
    loadBooking();
  }, [bookingId, bookingsFromStore]);

  // Check device trust for PIN authentication
  useEffect(() => {
    if (isAuthenticated) {
      setIsVerified(true);
      return;
    }

    if (!bookingId) {
      return;
    }

    // Check if device is trusted
    const trustInfo = isDeviceTrusted(bookingId);
    if (trustInfo) {
      console.log("✅ Device is trusted for CheckIn");
      setIsVerified(true);
    } else {
      console.log("🔒 Device not trusted, showing verification");
      setIsVerified(false);
    }
  }, [bookingId, isAuthenticated]);

  // Get products and settings from Zustand stores
  const products1FromStore = useProducts((state) => state.products1);
  const products2FromStore = useProducts((state) => state.products2);
  const settings = useSettings((state) => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);

  // Review request ask dialog — derived display values (see handleCompleteBooking)
  const reviewRequestHasEmail = !!(booking?.customerEmail || booking?.email);
  const reviewRequestDelayHours = Math.min(72, Math.max(1, Number(settings?.reviewRequest?.delayHours) || 4));

  // Update products catalogs when store changes (filter for hasRetailPrice)
  useEffect(() => {
    if (products1FromStore.length > 0) {
      const filtered = products1FromStore.filter(p => p.hasRetailPrice);
      setProducts1Catalog(filtered);
    }
  }, [products1FromStore]);

  useEffect(() => {
    if (products2FromStore.length > 0) {
      const filtered = products2FromStore.filter(p => p.hasRetailPrice);
      setProducts2Catalog(filtered);
    }
  }, [products2FromStore]);

  // Update settings when store changes
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      setStoreLogo(settings.storeLogo?.url || "");
      setProduct1CategoryName(settings.product1Title?.title || "Product1");
      setProduct2CategoryName(settings.product2Title?.title || "Product2");
      setShopInfo({
        name: settings.storeName?.name || "",
        phone: settings.storePhone?.phone || "",
        email: settings.storeEmail?.email || "",
      });
    }
  }, [settings]);

  // Auth state - handled by store

  // Get products from booking that have retail price
  const getProductsWithRetailPrice = (productList, catalog, type) => {
    if (!productList || !Array.isArray(productList)) return [];
    
    return productList
      .filter((item) => {
        const catalogProduct = catalog.find((p) => p.id === item.id);
        return catalogProduct && catalogProduct.hasRetailPrice;
      })
      .map((item) => {
        const catalogProduct = catalog.find((p) => p.id === item.id);
        return {
          ...item,
          retailPriceCents: catalogProduct.retailPriceCents || 0,
        };
      });
  };

  const product1Items = useMemo(() => {
    return getProductsWithRetailPrice(booking?.product1, products1Catalog, "product1");
  }, [booking?.product1, products1Catalog]);

  const product2Items = useMemo(() => {
    return getProductsWithRetailPrice(booking?.product2, products2Catalog, "product2");
  }, [booking?.product2, products2Catalog]);

  // Initialize all products as checked in (returnedProduct: true, no penalties) when page loads
  useEffect(() => {
    if (!booking || loading || (!product1Items.length && !product2Items.length)) return;
    
    // Check if we need to initialize products (only if they don't have intake entries)
    const needsInitialization = [...product1Items, ...product2Items].some((item) => {
      const type = product1Items.includes(item) ? "product1" : "product2";
      const intake = bookingIntake[type]?.[item.id];
      return !intake; // Product doesn't have an intake entry yet
    });
    
    if (needsInitialization) {
      updateBookingIntake((prev) => {
        const newIntake = { ...prev };
        
        // Initialize product1 items
        product1Items.forEach((item) => {
          if (!newIntake.product1[item.id]) {
            newIntake.product1[item.id] = {
              returnedProduct: true,
              penaltyType: "percentage",
              penaltyValue: 0,
              penaltyAmount: 0,
              penaltyReason: "",
              photos: [],
              messages: [],
              media: [],
            };
          }
        });
        
        // Initialize product2 items
        product2Items.forEach((item) => {
          if (!newIntake.product2[item.id]) {
            newIntake.product2[item.id] = {
              returnedProduct: true,
              penaltyType: "percentage",
              penaltyValue: 0,
              penaltyAmount: 0,
              penaltyReason: "",
              photos: [],
              messages: [],
              media: [],
            };
          }
        });
        
        return newIntake;
      });
    }
  }, [booking, product1Items, product2Items, bookingIntake, loading]);

  // Helper function to get media count (photos + videos from messages)
  const getMediaCount = (intake) => {
    if (!intake) return 0;
    const photosCount = intake.photos?.length || 0;
    const messagesCount = intake.messages?.filter(m => m.type === 'photo' || m.type === 'video').length || 0;
    const total = photosCount + messagesCount;
    // Debug log
    if (total > 0) {
      console.log("📊 Media count:", { photosCount, messagesCount, total, intake: intake });
    }
    return total;
  };

  // Helper function to get checkIn media count from booking (saved photos)
  const getCheckInMediaCount = () => {
    if (!booking?.media?.checkIn) return 0;
    return booking.media.checkIn.length;
  };


  // Toggle returned checkbox
  const toggleReturned = (productId, type) => {
    if (!isAuthenticated) return; // Disable for unauthenticated users
    updateBookingIntake((prev) => {
      const newIntake = { ...prev };
      if (!newIntake[type]) newIntake[type] = {};
      if (!newIntake[type][productId]) {
        newIntake[type][productId] = {
          returnedProduct: true,
          penaltyType: "percentage",
          penaltyValue: 0,
          penaltyAmount: 0,
          penaltyReason: "",
          photos: [],
          messages: [],
          media: [],
        };
      }
      
      newIntake[type][productId] = {
        ...newIntake[type][productId],
        returnedProduct: !newIntake[type][productId].returnedProduct,
      };
      
      return newIntake;
    });
  };

  // Return All / Clear All
  const handleReturnAll = () => {
    updateBookingIntake((prev) => {
      const newIntake = { ...prev };
      
      product1Items.forEach((item) => {
        if (!newIntake.product1[item.id]) {
          newIntake.product1[item.id] = {
            returnedProduct: true,
            penaltyType: "percentage",
            penaltyValue: 0,
            penaltyAmount: 0,
            reason: "",
            photos: [],
          };
        }
        newIntake.product1[item.id].returnedProduct = true;
      });
      
      product2Items.forEach((item) => {
        if (!newIntake.product2[item.id]) {
          newIntake.product2[item.id] = {
            returnedProduct: true,
            penaltyType: "percentage",
            penaltyValue: 0,
            penaltyAmount: 0,
            reason: "",
            photos: [],
          };
        }
        newIntake.product2[item.id].returnedProduct = true;
      });
      
      return newIntake;
    });
  };

  const handleClearAll = () => {
    updateBookingIntake((prev) => {
      const newIntake = { ...prev };
      
      product1Items.forEach((item) => {
        if (newIntake.product1[item.id]) {
          newIntake.product1[item.id].returnedProduct = false;
        }
      });
      
      product2Items.forEach((item) => {
        if (newIntake.product2[item.id]) {
          newIntake.product2[item.id].returnedProduct = false;
        }
      });
      
      return newIntake;
    });
  };

  // Handle penalty input change (from TextInput)
  const handlePenaltyInputChange = (productId, type, inputValue, penaltyType, retailPriceCents) => {
    // Remove non-numeric characters except decimal point
    const cleaned = inputValue.replace(/[^\d.]/g, '');
    if (cleaned === '' || cleaned === '.') {
      updatePenalty(productId, type, 0, penaltyType, retailPriceCents);
      return;
    }
    
    let numValue = parseFloat(cleaned);
    if (isNaN(numValue)) {
      numValue = 0;
    }
    
    // Clamp value based on penalty type
    if (penaltyType === "percentage") {
      numValue = Math.max(0, Math.min(100, numValue));
      updatePenalty(productId, type, numValue, penaltyType, retailPriceCents);
    } else {
      // For dollar amount, input is in dollars, convert to cents
      numValue = Math.max(0, Math.min(retailPriceCents / 100, numValue));
      const centsValue = Math.round(numValue * 100);
      updatePenalty(productId, type, centsValue, penaltyType, retailPriceCents);
    }
  };

  // Update penalty slider (state updates immediately for UI, autosave is already debounced)
  const updatePenalty = (productId, type, value, penaltyType, retailPriceCents) => {
    if (!isAuthenticated) return; // Disable for unauthenticated users
    
    // Update state immediately for responsive UI
    updateBookingIntake((prev) => {
      const newIntake = { ...prev };
      if (!newIntake[type]) newIntake[type] = {};
      if (!newIntake[type][productId]) {
        newIntake[type][productId] = {
          returnedProduct: true,
          penaltyType: "percentage",
          penaltyValue: 0,
          penaltyAmount: 0,
          penaltyReason: "",
          photos: [],
          messages: [],
          media: [],
        };
      }
      
      // Check if penalty type is changing - if so, reset value to 0
      const currentPenaltyType = newIntake[type][productId].penaltyType;
      const isPenaltyTypeChanging = currentPenaltyType !== penaltyType;
      const finalValue = isPenaltyTypeChanging ? 0 : value;
      
      let penaltyAmount = 0;
      if (penaltyType === "percentage") {
        penaltyAmount = Math.round((finalValue / 100) * retailPriceCents);
      } else {
        penaltyAmount = Math.round(finalValue);
      }
      
      newIntake[type][productId] = {
        ...newIntake[type][productId],
        penaltyType,
        penaltyValue: finalValue,
        penaltyAmount,
      };
      
      return newIntake;
    });
    // Autosave is already debounced via useEffect watching bookingIntake
  };

  // Save penalty reason (now uses penaltyReason field)
  const saveReason = (productId, type) => {
    if (!isAuthenticated) return; // Disable for unauthenticated users
    const key = `${type}_${productId}`;
    const reason = reasonTexts[key] || "";
    
    updateBookingIntake((prev) => {
      const newIntake = { ...prev };
      if (!newIntake[type]) newIntake[type] = {};
      if (!newIntake[type][productId]) {
        newIntake[type][productId] = {
          returnedProduct: true,
          penaltyType: "percentage",
          penaltyValue: 0,
          penaltyAmount: 0,
          penaltyReason: "",
          photos: [],
          messages: [],
          media: [],
        };
      }
      
      newIntake[type][productId].penaltyReason = reason;
      return newIntake;
    });
    
    setReasonModals((prev) => ({ ...prev, [key]: false }));
  };

  // Take photo
  const takePhoto = async (productId, type) => {
    if (!isAuthenticated) return; // Disable for unauthenticated users

    // Set customer in Zustand store from booking data so Photo page recognizes the customer
    if (booking) {
      const setCustomer = useCustomer.getState().setCustomer;
      const customerObj = {
        id: booking.id || bookingId,
        firstName: booking.firstName || "",
        lastName: booking.lastName || "",
        fullName: `${booking.firstName || ""} ${booking.lastName || ""}`.trim(),
        phone: booking.customerPhone || booking.phone || "",
        email: booking.customerEmail || booking.email || "",
        bookingId: bookingId,
        photoCount: 0,
        createdAt: booking.createdAt || new Date(),
      };
      setCustomer(customerObj);
      console.log("✅ Customer set from booking for Photo page:", customerObj.fullName);
    }

    // Navigate to Photo page with check-in context
    navigate("/photo", {
      state: {
        category: "checkIn",
        fromCheckIn: true,
        bookingId: bookingId,
        productId: productId,
        productType: type,
      },
    });
  };

  // Calculate total penalties
  // Only count items that have been explicitly edited (have an intake entry)
  const calculatePenalties = () => {
    let total = 0;
    const items = [];
    
    // Product1 items
    product1Items.forEach((item) => {
      const intake = bookingIntake.product1?.[item.id];
      // Only calculate if intake exists (item has been edited)
      if (intake) {
        if (!intake.returnedProduct) {
          // Not returned - full retail price
          total += item.retailPriceCents;
          items.push({
            name: item.name,
            amount: item.retailPriceCents,
            reason: "Not returned",
          });
        } else if (intake.penaltyAmount > 0) {
          // Returned but has penalty
          total += intake.penaltyAmount;
          items.push({
            name: item.name,
            amount: intake.penaltyAmount,
            reason: intake.penaltyReason || "Damage/Missing",
          });
        }
      }
      // If no intake entry, item hasn't been edited yet - don't count it
    });
    
    // Product2 items
    product2Items.forEach((item) => {
      const intake = bookingIntake.product2?.[item.id];
      // Only calculate if intake exists (item has been edited)
      if (intake) {
        if (!intake.returnedProduct) {
          // Not returned - full retail price
          total += item.retailPriceCents;
          items.push({
            name: item.name,
            amount: item.retailPriceCents,
            reason: "Not returned",
          });
        } else if (intake.penaltyAmount > 0) {
          // Returned but has penalty
          total += intake.penaltyAmount;
          items.push({
            name: item.name,
            amount: intake.penaltyAmount,
            reason: intake.penaltyReason || "Damage/Missing",
          });
        }
      }
      // If no intake entry, item hasn't been edited yet - don't count it
    });
    
    return { total, items };
  };

  const penalties = useMemo(() => calculatePenalties(), [bookingIntake, product1Items, product2Items]);

  // Check if all items are returned
  const allItemsReturned = useMemo(() => {
    const allItems = [...product1Items, ...product2Items];
    return allItems.every((item) => {
      const type = product1Items.includes(item) ? "product1" : "product2";
      const intake = bookingIntake[type]?.[item.id];
      return intake?.returnedProduct === true;
    });
  }, [bookingIntake, product1Items, product2Items]);

  // Live-save bookingIntake to database (new paradigm: all data in bookingIntake node)
  const liveSaveBookingIntake = async (intakeData) => {
    if (!bookingId || !isAuthenticated) return;

    try {
      const bookingsPath = getCollectionPath("bookings");
      const bookingRef = doc(db, bookingsPath, bookingId);

      // Get selected user for metadata
      const selectedUser = useAuthStore.getState().selectedUser;
      const userMetadata = selectedUser ? {
        userId: selectedUser.id,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        timestamp: Timestamp.now(),
      } : null;

      // Prepare bookingIntake structure: each product has penaltyAmount, penaltyType, penaltyValue, returnedProduct, penaltyReason, media
      const newBookingIntake = {
        product1: {},
        product2: {},
      };

      // Add user metadata to bookingIntake
      if (userMetadata) {
        newBookingIntake.lastUpdatedBy = userMetadata;
      }

      // Process product1 items
      if (intakeData.product1) {
        Object.keys(intakeData.product1).forEach((productId) => {
          const item = intakeData.product1[productId];

          newBookingIntake.product1[productId] = {
            penaltyAmount: item.penaltyAmount || 0,
            penaltyType: item.penaltyType || "percentage",
            penaltyValue: item.penaltyValue || 0,
            returnedProduct: item.returnedProduct !== undefined ? item.returnedProduct : true,
            penaltyReason: item.penaltyReason || "",
            media: item.media || [], // Array of photo/video URLs
          };
        });
      }

      // Process product2 items
      if (intakeData.product2) {
        Object.keys(intakeData.product2).forEach((productId) => {
          const item = intakeData.product2[productId];

          newBookingIntake.product2[productId] = {
            penaltyAmount: item.penaltyAmount || 0,
            penaltyType: item.penaltyType || "percentage",
            penaltyValue: item.penaltyValue || 0,
            returnedProduct: item.returnedProduct !== undefined ? item.returnedProduct : true,
            penaltyReason: item.penaltyReason || "",
            media: item.media || [], // Array of photo/video URLs
          };
        });
      }

      // Preserve flaggedForReview if it exists
      if (intakeData.flaggedForReview !== undefined) {
        newBookingIntake.flaggedForReview = intakeData.flaggedForReview;
      }

      const updateData = {
        bookingIntake: newBookingIntake,
        updatedAt: Timestamp.now(),
      };

      // Add change tracking if user metadata is available
      if (userMetadata) {
        const changeEntry = createChangeTrackingEntry(userMetadata, {
          changeType: "check_in",
          fieldChanged: "bookingIntake",
          description: "Check-in data updated",
          timestamp: Timestamp.now(),
        });
        
        if (changeEntry) {
          updateData.editedBy = arrayUnion(changeEntry);
        }
      }

      await updateDoc(bookingRef, updateData);

      console.log("✅ BookingIntake live-saved to database");
    } catch (error) {
      console.error("❌ Error live-saving booking intake:", error);
      // Don't throw - live-save failures shouldn't block UI
    }
  };

  // Debounced live-save to avoid too many writes
  const liveSaveTimeoutRef = useRef(null);
  const lastSavedIntakeRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  const debouncedLiveSave = (intakeData) => {
    // Skip save during initial load
    if (isInitialLoadRef.current) {
      return;
    }

    // Serialize current intake for comparison
    const currentIntakeStr = JSON.stringify(intakeData);
    const lastSavedStr = lastSavedIntakeRef.current ? JSON.stringify(lastSavedIntakeRef.current) : null;

    // Skip save if data hasn't actually changed
    if (currentIntakeStr === lastSavedStr) {
      return;
    }

    if (liveSaveTimeoutRef.current) {
      clearTimeout(liveSaveTimeoutRef.current);
    }
    liveSaveTimeoutRef.current = setTimeout(() => {
      liveSaveBookingIntake(intakeData);
      // Update last saved reference after successful save
      lastSavedIntakeRef.current = JSON.parse(JSON.stringify(intakeData));
    }, 1000); // Increased debounce to 1 second
  };

  // Auto-save bookingIntake whenever it changes (but not during initial load)
  useEffect(() => {
    if (bookingIntake && Object.keys(bookingIntake).length > 0) {
      // Mark initial load as complete after first render
      if (isInitialLoadRef.current) {
        // Set last saved to current to prevent immediate save
        lastSavedIntakeRef.current = JSON.parse(JSON.stringify(bookingIntake));
        isInitialLoadRef.current = false;
        return;
      }

      debouncedLiveSave(bookingIntake);
    }
  }, [bookingIntake]);


  // Delete media from a product
  const handleDeleteMedia = async (productId, type, mediaUrl) => {
    if (!isAuthenticated || !bookingId) return;

    if (!window.confirm("Are you sure you want to delete this media?")) {
      return;
    }

    try {
      // Remove from bookingIntake state
      updateBookingIntake((prev) => {
        const newIntake = { ...prev };
        if (newIntake[type] && newIntake[type][productId]) {
          newIntake[type][productId].media = (newIntake[type][productId].media || []).filter(url => url !== mediaUrl);
        }
        return newIntake;
      });

      // Remove from Firestore bookingIntake
      const bookingsPath = getCollectionPath("bookings");
      const bookingRef = doc(db, bookingsPath, bookingId);
      const bookingDoc = await getDoc(bookingRef);

      if (bookingDoc.exists()) {
        const bookingData = bookingDoc.data();
        const currentIntake = bookingData.bookingIntake || { product1: {}, product2: {} };

        // Update the specific product's media array
        if (currentIntake[type] && currentIntake[type][productId]) {
          currentIntake[type][productId].media = (currentIntake[type][productId].media || []).filter(url => url !== mediaUrl);

          await updateDoc(bookingRef, {
            bookingIntake: currentIntake,
            updatedAt: Timestamp.now(),
          });
        }

        // Also remove from booking.media.checkIn array if it exists
        if (bookingData.media && bookingData.media.checkIn && Array.isArray(bookingData.media.checkIn)) {
          const { arrayRemove } = await import('firebase/firestore');
          // Find the media item by URL
          const mediaItem = bookingData.media.checkIn.find(item => item.url === mediaUrl);
          if (mediaItem) {
            await updateDoc(bookingRef, {
              [`media.checkIn`]: arrayRemove(mediaItem),
              updatedAt: Timestamp.now(),
            });
          }
        }
      }

      console.log("✅ Media deleted successfully");
    } catch (error) {
      console.error("❌ Error deleting media:", error);
      alert("Failed to delete media. Please try again.");
    }
  };

  // Charge penalties
  const handleChargePenalties = () => {
    // Show confirmation dialog
    const uncheckedProducts = getUncheckedProducts();
    let confirmMessage = `Are you sure you want to charge ${formatCentsAsUSD(penalties.total)} in penalties?`;
    
    if (uncheckedProducts.length > 0) {
      confirmMessage += `\n\n⚠️ Warning: ${uncheckedProducts.length} item(s) not checked:\n`;
      uncheckedProducts.forEach(item => {
        confirmMessage += `• ${item.name}\n`;
      });
      confirmMessage += '\nProceed anyway?';
    }
    
    if (window.confirm(confirmMessage)) {
      setChargeModalVisible(true);
    }
  };

  // Flag for review
  const handleSaveForReview = async () => {
    // Check if already flagged
    const isFlagged = bookingIntake?.flaggedForReview === true;

    if (isFlagged) {
      // If already flagged, just navigate back
      navigate("/back-office");
      return;
    }

    // Show confirmation dialog
    if (window.confirm("Are you sure you want to flag this check-in for review? You can return to complete it later.")) {
      try {
        setSaveForReviewProcessing(true);
        const bookingsPath = getCollectionPath("bookings");
        const bookingRef = doc(db, bookingsPath, bookingId);

        // Get selected user for metadata
        const selectedUser = useAuthStore.getState().selectedUser;
        const userMetadata = selectedUser ? {
          userId: selectedUser.id,
          firstName: selectedUser.firstName || "",
          lastName: selectedUser.lastName || "",
          timestamp: Timestamp.now(),
        } : null;

        await updateDoc(bookingRef, {
          bookingIntake: {
            ...bookingIntake,
            flaggedForReview: true,
            lastUpdatedBy: userMetadata,
          },
          updatedAt: Timestamp.now(),
        });
        
        // Update local state
        updateBookingIntake((prev) => ({
          ...prev,
          flaggedForReview: true,
        }));

        alert("Check-in flagged successfully!");
        navigate("/back-office");
      } catch (error) {
        console.error("Error flagging for review:", error);
        alert("Failed to flag check-in. Please try again.");
      } finally {
        setSaveForReviewProcessing(false);
      }
    }
  };

  const handleChargeConfirm = async () => {
    if (chargeSliderPosition < 100) {
      return; // Not fully swiped
    }
    
    try {
      setChargeProcessing(true);
      
      // Get first payment method
      const firstPayment = booking.payments?.[0];
      if (!firstPayment) {
        throw new Error("No payment method found");
      }
      
      // Get last4 from cardInfo if available
      const last4 = firstPayment.cardInfo?.last4 || firstPayment.last4 || "";
      
      // Save bookingIntake concurrently with payment processing
      const bookingsPath = getCollectionPath("bookings");
      const bookingRef = doc(db, bookingsPath, bookingId);

      // Get selected user for metadata
      const selectedUser = useAuthStore.getState().selectedUser;
      const userMetadata = selectedUser ? {
        userId: selectedUser.id,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        timestamp: Timestamp.now(),
      } : null;

      // Start both operations concurrently
      const chargeCard = httpsCallable(functions, "chargeSavedCard");
      const chargePromise = chargeCard({
        bookingId: booking.id,
        amountCents: penalties.total,
        description: "Equipment damage/missing items penalty",
        last4: last4,
        tenantId: getTenantId(),
        storeId: getStoreId(),
        testMode: useTestStripe,
      });
      
      const updateData = {
        bookingIntake: {
          ...bookingIntake,
          lastUpdatedBy: userMetadata,
        },
        updatedAt: Timestamp.now(),
      };

      // Add change tracking if user metadata is available
      if (userMetadata) {
        const changeEntry = createChangeTrackingEntry(userMetadata, {
          changeType: "check_in",
          fieldChanged: "bookingIntake",
          description: "Check-in completed with penalties",
          timestamp: Timestamp.now(),
        });
        
        if (changeEntry) {
          updateData.editedBy = arrayUnion(changeEntry);
        }
      }

      const saveIntakePromise = updateDoc(bookingRef, updateData);
      
      // Wait for both to complete
      const [result] = await Promise.all([chargePromise, saveIntakePromise]);
      
      if (result.data.success) {
        setChargeSuccess(true);
        
        // Save penalty payment information to booking object
        const penaltyPayment = {
          paymentIntentId: result.data.paymentIntentId,
          amountCents: result.data.amount,
          currency: result.data.currency,
          receiptUrl: result.data.receiptUrl,
          charges: result.data.charges || [],
          processedAt: Timestamp.now(),
        };
        
        const penaltyUpdateData = {
          penaltyPayment,
          updatedAt: Timestamp.now(),
        };

        // Add change tracking if user metadata is available
        if (userMetadata) {
          const changeEntry = createChangeTrackingEntry(userMetadata, {
            changeType: "check_in",
            fieldChanged: "penaltyPayment",
            description: `Penalty payment processed: ${formatCentsAsUSD(penalties.total)}`,
            timestamp: Timestamp.now(),
          });
          
          if (changeEntry) {
            penaltyUpdateData.editedBy = arrayUnion(changeEntry);
          }
        }

        await updateDoc(bookingRef, penaltyUpdateData);
        
        // Wait 1 second to show success, then start sending notifications
        setTimeout(async () => {
          setChargeSuccess(false);
          setSendingNotifications(true);
          
          // Send completion messages (with penalty)
          try {
            await sendCompletionMessages(true);
          } catch (messageError) {
            console.error("Error sending completion messages:", messageError);
            // Don't fail the charge if messaging fails
          }
          
          // Send penalty notifications using editable templates
          try {
            await sendPenaltyNotifications(result.data.receiptUrl);
          } catch (notificationError) {
            console.error("Error sending penalty notifications:", notificationError);
            // Don't fail the charge if notifications fail
          }
          
          // Close modal after notifications are sent
          setSendingNotifications(false);
          setChargeModalVisible(false);
          setChargeProcessing(false);
          setChargeSliderPosition(0);
        }, 1000);
      } else {
        throw new Error(result.data.error || "Charge failed");
      }
    } catch (error) {
      console.error("Error charging penalties:", error);
      alert("Error charging penalties: " + error.message);
      setChargeProcessing(false);
    }
  };

  // Send penalty notifications using editable templates
  const sendPenaltyNotifications = async (receiptUrl) => {
    try {
      // Load penalty email template from Firestore
      const settingsPath = getSettingsPath();
      const emailTemplateRef = doc(db, settingsPath, "emailPenaltyTemplateFields");
      const emailTemplateDoc = await getDoc(emailTemplateRef);
      
      const emailTemplate = emailTemplateDoc.exists() 
        ? emailTemplateDoc.data()
        : {
            headerSubtitle: "We hope you had a great rental!",
            greetingText: "Thank you for choosing {storeName}! We've completed your check-in and found it necessary to charge for some equipment-related items.",
            customInstructions: "We've uploaded photos and details to your check-in page so you can see what we found. You can view everything by clicking the button below.",
            buttonText: "📋 View Check-In Details",
            footerMessage: "If you have any questions about these charges, please don't hesitate to contact us. We appreciate your business!",
          };

      // Load penalty SMS template from Firestore
      const smsTemplateRef = doc(db, settingsPath, "penaltySmsTemplate");
      const smsTemplateDoc = await getDoc(smsTemplateRef);
      
      const smsTemplateData = smsTemplateDoc.exists()
        ? (smsTemplateDoc.data().template || "Hi {customerName}! We charged {penaltyAmount} for equipment penalties. Photos & details available here: {checkInUrl} Receipt: {receiptUrl}")
        : "Hi {customerName}! We charged {penaltyAmount} for equipment penalties. Photos & details available here: {checkInUrl} Receipt: {receiptUrl}";

      const storeName = settings?.storeName?.name || "We";
      const customerName = capitalizeName(`${booking.firstName} ${booking.lastName}`);
      const customerEmail = booking.customerEmail || booking.email;
      const customerPhone = booking.customerPhone || booking.phone;
      const checkInUrl = `${RENTAL_APP_BASE_URL}/check-in/${bookingId}`;
      const penaltyAmount = formatCentsAsUSD(penalties.total);
      
      // Build itemized penalty list for email
      const penaltyItemsHtml = penalties.items.map(item => 
        `<div style="padding: 8px 0; border-bottom: 1px solid #eee;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-weight: 600;">${item.name}</span>
            <span style="color: #dc3545; font-weight: bold;">${formatCentsAsUSD(item.amount)}</span>
          </div>
          ${item.penaltyReason ? `<div style="color: #666; font-size: 13px; margin-top: 4px;">${item.penaltyReason}</div>` : ""}
        </div>`
      ).join("");
      
      // Replace variables in email template fields
      const replaceVars = (text) => {
        return (text || "")
          .replace(/{storeName}/g, storeName)
          .replace(/{customerName}/g, customerName)
          .replace(/{penaltyAmount}/g, penaltyAmount)
          .replace(/{checkInUrl}/g, checkInUrl)
          .replace(/{receiptUrl}/g, receiptUrl || "");
      };

      // Build complete email HTML
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
          <!-- Header -->
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center;">
            <h1 style="color: #ffffff; margin: 0; font-size: 24px;">🚴 Equipment Check-In Complete</h1>
            <p style="color: #ffffff; margin: 8px 0 0 0; opacity: 0.95; font-size: 14px;">
              ${replaceVars(emailTemplate.headerSubtitle)}
            </p>
          </div>

          <!-- Greeting -->
          <div style="padding: 20px;">
            <p style="font-size: 15px; color: #333; margin-bottom: 12px;">Hi <strong>${customerName}</strong>,</p>
            <p style="font-size: 15px; color: #333; line-height: 22px; margin-bottom: 20px;">
              ${replaceVars(emailTemplate.greetingText)}
            </p>
          </div>

          <!-- Penalty Items -->
          <div style="margin: 20px; background-color: #fff3cd; border-radius: 8px; border-left: 4px solid #ffc107; padding: 15px;">
            <h3 style="margin-top: 0; color: #856404; font-size: 16px;">💰 Charged Items:</h3>
            ${penaltyItemsHtml}
            <div style="margin-top: 15px; padding-top: 15px; border-top: 2px solid #ffc107;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 16px; font-weight: bold; color: #333;">Total Charged:</span>
                <span style="font-size: 18px; font-weight: bold; color: #dc3545;">${penaltyAmount}</span>
              </div>
            </div>
          </div>

          <!-- Custom Instructions -->
          ${emailTemplate.customInstructions ? `
          <div style="padding: 20px;">
            <p style="font-size: 14px; color: #333; line-height: 20px; background-color: #e3f2fd; padding: 15px; border-radius: 8px; border-left: 4px solid #2196F3;">
              ${replaceVars(emailTemplate.customInstructions)}
            </p>
          </div>` : ""}

          <!-- Stripe Receipt Link -->
          ${receiptUrl ? `
          <div style="padding: 0 20px 20px 20px; text-align: center;">
            <a href="${receiptUrl}" style="display: inline-block; background-color: #6c757d; color: white; padding: 10px 24px; text-decoration: none; border-radius: 8px; font-size: 14px; font-weight: 600;">
              📄 View Stripe Receipt
            </a>
          </div>` : ""}

          <!-- Button -->
          <div style="padding: 20px; text-align: center;">
            <a href="${checkInUrl}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 40px; text-decoration: none; border-radius: 25px; font-size: 15px; font-weight: bold; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);">
              ${replaceVars(emailTemplate.buttonText)}
            </a>
            <p style="font-size: 12px; color: #666; margin-top: 12px;">
              View photos, reasons, and all penalty details
            </p>
          </div>

          <!-- Contact Info -->
          <div style="margin: 20px; padding: 20px; background-color: #f8f9fa; border-radius: 8px; text-align: center; border-top: 1px solid #e0e0e0;">
            <h4 style="font-size: 15px; font-weight: 600; color: #333; margin: 0 0 12px 0;">📞 Questions or Need Help?</h4>
            <p style="font-size: 13px; color: #666; margin: 6px 0;">📱 Phone: ${shopInfo.phone || "Contact us"}</p>
            <p style="font-size: 13px; color: #666; margin: 6px 0;">📧 Email: ${shopInfo.email || "Contact us"}</p>
          </div>

          <!-- Footer Message -->
          ${emailTemplate.footerMessage ? `
          <div style="padding: 20px;">
            <p style="font-size: 14px; color: #333; text-align: center; font-style: italic;">
              ${replaceVars(emailTemplate.footerMessage)}
            </p>
          </div>` : ""}

          <!-- Footer -->
          <div style="background-color: #f8f9fa; padding: 20px; text-align: center;">
            <p style="font-size: 11px; color: #999; line-height: 16px; margin: 0;">
              © ${new Date().getFullYear()} ${storeName}. All rights reserved.<br>
              This email was sent to ${customerEmail}
            </p>
          </div>
        </div>
      `;
      
      // Send email
      if (customerEmail) {
        try {
          const sendEmailFunction = httpsCallable(functions, "sendEmail");
          await sendEmailFunction({
            to: customerEmail,
            subject: `Equipment Penalty Charge - ${storeName}`,
            html: emailHtml,
            tenantId: getTenantId(),
            storeId: getStoreId(),
          });
          console.log("✅ Penalty email sent successfully");
        } catch (emailError) {
          console.error("❌ Error sending penalty email:", emailError);
        }
      }
      
      // Send SMS using template
      if (customerPhone) {
        try {
          const smsMessage = smsTemplateData
            .replace(/{customerName}/g, capitalizeName(booking.firstName || customerName))
            .replace(/{penaltyAmount}/g, penaltyAmount)
            .replace(/{checkInUrl}/g, checkInUrl)
            .replace(/{receiptUrl}/g, receiptUrl || "");

          const sendSMSFunction = httpsCallable(functions, "sendSMS");
          await sendSMSFunction({
            to: customerPhone,
            message: smsMessage,
            customerName: customerName,
            customerPhone: customerPhone,
            tenantId: getTenantId(),
            storeId: getStoreId(),
            sentByUserId: getSelectedUserId(),
          });
          console.log("✅ Penalty SMS sent successfully");
        } catch (smsError) {
          console.error("❌ Error sending penalty SMS:", smsError);
        }
      }
    } catch (error) {
      console.error("❌ Error sending penalty notifications:", error);
      // Don't throw - notification failure shouldn't block the charge
    }
  };

  // Save bookingIntake to database (wrapper for liveSaveBookingIntake)
  const saveBookingIntake = async () => {
    await liveSaveBookingIntake(bookingIntake);
  };

  // Complete booking (no penalties)
  const handleCompleteBooking = async () => {
    console.log("✅ Starting handleCompleteBooking...");
    
    try {
      setCompleteBookingProcessing(true);
      
      const bookingsPath = getCollectionPath("bookings");
      const completedBookingsPath = getCollectionPath("completedBookings");
      
      console.log("📁 Paths:", { bookingsPath, completedBookingsPath, bookingId });
      
      const bookingRef = doc(db, bookingsPath, bookingId);
      
      // Get selected user for metadata
      const selectedUser = useAuthStore.getState().selectedUser;
      const userMetadata = selectedUser ? {
        userId: selectedUser.id,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        timestamp: Timestamp.now(),
      } : null;

      // Save bookingIntake (with media separated) to the active booking first
      console.log("💾 Saving bookingIntake...");
      await saveBookingIntake();
      console.log("✅ bookingIntake saved");
      
      // Update bookingIntake with user metadata before moving to completed
      if (userMetadata) {
        await updateDoc(bookingRef, {
          "bookingIntake.lastUpdatedBy": userMetadata,
          updatedAt: Timestamp.now(),
        });
      }

      // Get the latest booking data (including the just-saved bookingIntake)
      console.log("📖 Getting latest booking data...");
      const latestBookingDoc = await getDoc(bookingRef);
      if (!latestBookingDoc.exists()) {
        throw new Error("Booking not found");
      }
      
      const latestBookingData = latestBookingDoc.data();
      console.log("✅ Latest booking data retrieved");
      
      // Prepare booking data for completedBookings
      const completedBookingData = { 
        ...latestBookingData,
        bookingIntake: bookingIntake,
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      // Copy booking to completedBookings
      console.log("📦 Moving booking to completedBookings...");
      const completedBookingRef = doc(db, completedBookingsPath, bookingId);
      await setDoc(completedBookingRef, completedBookingData);
      console.log("✅ Booking moved to completedBookings");
      
      // Delete from active bookings
      console.log("🗑️ Deleting from active bookings...");
      await deleteDoc(bookingRef);
      console.log("✅ Deleted from active bookings");
      
      // Send completion messages (check if penalties exist)
      console.log("📧 Sending completion messages...");
      const hasPenalties = penalties.total > 0;
      await sendCompletionMessages(hasPenalties);
      console.log("✅ Completion messages sent");

      // Check review-request eligibility (never blocks completion on error)
      const reviewEligible = await checkReviewRequestEligibility();

      setCompleteBookingProcessing(false);

      if (reviewEligible) {
        // Defer navigation — the review-ask dialog's buttons navigate on either answer
        setCompleteBookingModalVisible(false);
        setReviewAskVisible(true);
        return;
      }

      // Close modal and reset state
      setCompleteBookingModalVisible(false);

      // Navigate back to back office
      navigate("/back-office");
    } catch (error) {
      console.error("❌ Error completing booking:", error);
      alert("❌ Error completing booking: " + error.message);
      setCompleteBookingProcessing(false);
      setCompleteBookingModalVisible(false);
    }
  };

  // Eligibility check for the post-completion "ask for a review" dialog.
  // Wrapped so an error here can never block check-in completion.
  const checkReviewRequestEligibility = async () => {
    try {
      const rr = settings?.reviewRequest || {};
      if (rr.enabled !== true) return false;
      if (!(rr.url || "").trim()) return false;

      const phone = (booking.customerPhone || booking.phone || "").replace(/\D/g, "");
      const cleanPhone = phone.length === 10 ? phone : "";
      const email = (booking.customerEmail || booking.email || "").trim();
      if (!cleanPhone && !email) return false;

      const contactKey = cleanPhone || email.toLowerCase();
      const logPath = getCollectionPath("reviewRequestLog");
      const logDoc = await getDoc(doc(db, logPath, contactKey));

      // 0 is a valid, deliberate value ("ask every time") — must not coerce to the default.
      const rawSuppressDays = Number(rr.reAskSuppressDays);
      const suppressDays = Number.isFinite(rawSuppressDays)
        ? Math.min(365, Math.max(0, rawSuppressDays))
        : 3;

      if (suppressDays > 0 && logDoc.exists()) {
        const lastAskedMillis = logDoc.data()?.lastAskedMillis || 0;
        const windowMs = suppressDays * 86400000;
        if (Date.now() - lastAskedMillis < windowMs) {
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Error checking review request eligibility:", error);
      return false;
    }
  };

  const handleReviewAskDecline = () => {
    setReviewAskVisible(false);
    navigate("/back-office");
  };

  const handleReviewAskAccept = async () => {
    try {
      setReviewAskBusy(true);
      const selectedUser = useAuthStore.getState().selectedUser;
      const userMeta = selectedUser
        ? {
            userId: selectedUser.id,
            firstName: selectedUser.firstName || "",
            lastName: selectedUser.lastName || "",
          }
        : null;

      const result = await scheduleReviewRequest(booking, settings, userMeta);
      if (result.success) {
        alert("Review link scheduled!");
      } else {
        console.error("Failed to schedule review request:", result.error);
      }
    } catch (error) {
      console.error("Error scheduling review request:", error);
    } finally {
      setReviewAskBusy(false);
      setReviewAskVisible(false);
      navigate("/back-office");
    }
  };

  // Send completion messages (SMS and Email) based on penalty status
  const sendCompletionMessages = async (hasPenalties) => {
    try {
      const storeName = settings?.storeName?.name || "We";
      const companyWebsite = settings?.companyWebsite?.url || RENTAL_APP_BASE_URL;
      const bookingUrl = `${RENTAL_APP_BASE_URL}/view-booking/${bookingId}`;
      const customerName = capitalizeName(`${booking.firstName} ${booking.lastName}`);
      const customerPhone = booking.customerPhone || booking.phone;
      const customerEmail = booking.customerEmail || booking.email;
      
      let smsMessage = "";
      let emailSubject = "";
      let emailBody = "";
      
      if (hasPenalties) {
        // Message with penalty
        const penaltyAmount = formatCentsAsUSD(penalties.total);
        smsMessage = `Message from ${storeName}, we have completed your booking! We found it necessary by the terms of the waiver agreement to charge ${penaltyAmount} for damaged items. Please visit your booking page to see what we found: ${bookingUrl}`;
        
        emailSubject = `Your ${storeName} Booking Completion - Penalty Charge`;
        emailBody = `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #333;">Booking Completion Notice</h2>
            <p>Hi ${customerName},</p>
            <p>Message from ${storeName}, we have completed your booking! We found it necessary by the terms of the waiver agreement to charge ${penaltyAmount} for damaged items. Please visit your booking page to see what we found.</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${bookingUrl}" style="background-color: #007AFF; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block;">
                View Your Booking
              </a>
            </div>
            <p style="color: #666; font-size: 14px;">Thank you for your business!</p>
          </div>
        `;
      } else {
        // Message without penalty
        smsMessage = `Message from ${storeName}, we have completed your booking intake! Thank you for taking care of and returning our equipment, we hope to see you again for your next adventure! ${companyWebsite}`;
        
        emailSubject = `Your ${storeName} Booking Completion`;
        emailBody = `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #333;">Booking Completion</h2>
            <p>Hi ${customerName},</p>
            <p>Message from ${storeName}, we have completed your booking intake! Thank you for taking care of and returning our equipment, we hope to see you again for your next adventure!</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${companyWebsite}" style="background-color: #007AFF; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block;">
                Visit Our Website
              </a>
            </div>
            <p style="color: #666; font-size: 14px;">Thank you for your business!</p>
          </div>
        `;
      }
      
      // Send SMS
      if (customerPhone) {
        try {
          const sendSMS = httpsCallable(functions, "sendSMS");
          await sendSMS({
            to: customerPhone,
            message: smsMessage,
            customerName: customerName,
            customerPhone: customerPhone,
            tenantId: getTenantId(),
            storeId: getStoreId(),
            sentByUserId: getSelectedUserId(),
          });
          console.log("✅ SMS sent successfully");
        } catch (smsError) {
          console.error("❌ Failed to send SMS:", smsError);
        }
      }
      
      // Send Email via cloud function (will be created to follow existing pattern)
      if (customerEmail) {
        try {
          const sendCheckInEmail = httpsCallable(functions, "sendCheckInCompletionEmail");
          await sendCheckInEmail({
            to: customerEmail,
            subject: emailSubject,
            htmlBody: emailBody,
            customerName: customerName,
            tenantId: getTenantId(),
            storeId: getStoreId(),
          });
          console.log("✅ Email sent successfully");
        } catch (emailError) {
          console.error("❌ Failed to send email:", emailError);
        }
      }
    } catch (error) {
      console.error("Error sending completion messages:", error);
      throw error;
    }
  };

  // Send thank you messages using templates from settings (DEPRECATED - use sendCompletionMessages instead)
  const sendThankYouMessages = async () => {
    try {
      // Load thank you email template from settings
      const settingsPath = getSettingsPath();
      const emailTemplateRef = doc(db, settingsPath, "emailThankYouTemplateFields");
      const emailTemplateDoc = await getDoc(emailTemplateRef);
      
      const emailFields = emailTemplateDoc.exists() 
        ? emailTemplateDoc.data()
        : {
            headerSubtitle: "Thank you for choosing {storeName}!",
            greetingText: "Thank you for your rental! We hope you had a wonderful experience. We'd love to hear your feedback!",
            buttonText: "📋 View Your Booking",
            footerMessage: "",
          };
      
      // Load thank you SMS template
      const smsTemplateRef = doc(db, settingsPath, "thankYouSmsTemplate");
      const smsTemplateDoc = await getDoc(smsTemplateRef);
      
      const smsTemplate = smsTemplateDoc.exists() && smsTemplateDoc.data().template
        ? smsTemplateDoc.data().template
        : "🙏 Thank you for choosing {storeName}! We hope you had a great experience. View your booking: {bookingUrl}";
      
      // Replace template variables
      const bookingUrl = `${RENTAL_APP_BASE_URL}/booking/${booking.id}`;
      const customerName = capitalizeName(`${booking.firstName} ${booking.lastName}`);
      const storeName = shopInfo.name || "us";
      
      let emailBody = emailFields.greetingText || "";
      emailBody = emailBody.replace(/{storeName}/g, storeName);
      emailBody = emailBody.replace(/{customerName}/g, customerName);
      emailBody = emailBody.replace(/{bookingUrl}/g, bookingUrl);
      
      if (emailFields.customInstructions) {
        emailBody += "\n\n" + emailFields.customInstructions
          .replace(/{storeName}/g, storeName)
          .replace(/{bookingUrl}/g, bookingUrl);
      }
      
      if (emailFields.footerMessage) {
        emailBody += "\n\n" + emailFields.footerMessage.replace(/{storeName}/g, storeName);
      }
      
      // Build HTML email
      const htmlEmail = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #667eea;">${emailFields.headerSubtitle?.replace(/{storeName}/g, storeName) || "Thank You!"}</h2>
          <p>Hi ${customerName},</p>
          <p>${emailBody.split("\n\n")[0]}</p>
          ${emailFields.customInstructions ? `<p>${emailFields.customInstructions.replace(/{storeName}/g, storeName).replace(/{bookingUrl}/g, bookingUrl)}</p>` : ""}
          <div style="text-align: center; margin: 30px 0;">
            <a href="${bookingUrl}" style="background-color: #007AFF; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block;">
              ${emailFields.buttonText || "View Your Booking"}
            </a>
          </div>
          ${emailFields.footerMessage ? `<p>${emailFields.footerMessage.replace(/{storeName}/g, storeName)}</p>` : ""}
        </div>
      `;
      
      // Send thank you email
      try {
        const sendEmailFunction = httpsCallable(functions, "sendEmail");
        await sendEmailFunction({
          to: booking.customerEmail,
          subject: emailFields.headerSubtitle?.replace(/{storeName}/g, storeName) || "Thank You!",
          html: htmlEmail,
        });
      } catch (emailError) {
        console.error("Error sending thank you email:", emailError);
        // Don't fail if email sending fails
      }
      
      // Replace SMS template variables
      let smsMessage = smsTemplate;
      smsMessage = smsMessage.replace(/{storeName}/g, storeName);
      smsMessage = smsMessage.replace(/{customerName}/g, capitalizeName(booking.firstName));
      smsMessage = smsMessage.replace(/{bookingUrl}/g, bookingUrl);
      
      // Send thank you SMS
      try {
        const sendSMSFunction = httpsCallable(functions, "sendSMS");
        await sendSMSFunction({
          to: booking.customerPhone,
          message: smsMessage,
          sentByUserId: getSelectedUserId(),
        });
      } catch (smsError) {
        console.error("Error sending thank you SMS:", smsError);
        // Don't fail if SMS sending fails
      }
    } catch (error) {
      console.error("Error sending thank you messages:", error);
      // Don't throw - message sending failure shouldn't block completion
    }
  };

  // Get unchecked products for modal
  const getUncheckedProducts = () => {
    const unchecked = [];
    
    product1Items.forEach((item) => {
      const intake = bookingIntake.product1?.[item.id];
      if (!intake || !intake.returnedProduct) {
        unchecked.push({ ...item, type: "product1" });
      }
    });
    
    product2Items.forEach((item) => {
      const intake = bookingIntake.product2?.[item.id];
      if (!intake || !intake.returnedProduct) {
        unchecked.push({ ...item, type: "product2" });
      }
    });
    
    return unchecked;
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0E7C6B" />
        <Text style={styles.loadingText}>Loading booking...</Text>
      </View>
    );
  }

  if (!booking) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Booking not found</Text>
      </View>
    );
  }

  // Show PIN auth layer for unauthenticated users
  if (!isAuthenticated && !isVerified && bookingId) {
    return (
      <View style={{ flex: 1, backgroundColor: "#F6F8F7" }}>
        <AuthHeader
          title="Check In"
          subtitle=""
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={{}}
          storeLogo=""
        />
        <PinAuthLayer
          onAuthenticated={() => {
            console.log("✅ PIN verified for CheckIn:", bookingId);
            setIsVerified(true);
          }}
          bookingId={bookingId}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Check In Equipment"
        subtitle={shopInfo.name ? `by ${shopInfo.name}` : ""}
        isDesktop={window.innerWidth >= 768}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />

      <View style={window.innerWidth >= 768 ? styles.desktopWrapper : null}>
      <ScrollView style={styles.scrollView}>
        {/* Top Action Buttons */}
        <View style={styles.topButtonsContainer}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.topButton, styles.viewBookingButton]}
            onPress={() => navigate(`/view-booking/${bookingId}`)}
          >
            <Text style={styles.topButtonText}>
              👁️ View Booking
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.topButton, styles.photosVideosButtonTop]}
            onPress={() => navigate(`/all-media/${bookingId}`)}
          >
            <Text style={styles.topButtonText}>
              📷 Photos & Videos
            </Text>
          </TouchableOpacity>
        </View>

        {/* Auto-Save Indicator */}
        {isAuthenticated && (
          <View style={styles.autoSaveIndicator}>
            <Text style={styles.autoSaveIcon}>💾</Text>
            <Text style={styles.autoSaveText}>Auto-Save is On</Text>
          </View>
        )}

        {/* Product1 Items */}
        {product1Items.length > 0 && (
          <View style={[styles.categoryCard, styles.categoryCardGreen]}>
            {/* Badge */}
            <View style={[styles.categoryBadge, { backgroundColor: "#1F8A5B" }]}>
              <Text style={styles.categoryBadgeText}>
                {product1CategoryName}
              </Text>
            </View>
            {product1Items.map((item) => {
              const intake = bookingIntake.product1?.[item.id] || {
                returnedProduct: true,
                penaltyType: "percentage",
                penaltyValue: 0,
                penaltyAmount: 0,
                penaltyReason: "",
                photos: [],
                messages: [],
                media: [],
              };
              
              const showImage = showImages[`product1_${item.id}`] || false;
              const reasonModalKey = `product1_${item.id}`;
              const reasonModalVisible = reasonModals[reasonModalKey] || false;
              
              const catalogProduct = products1Catalog.find((p) => p.id === item.id);
              
              return (
                <View key={item.id} style={styles.productCard}>
                  {/* Product Name and Image Toggle */}
                  <View style={styles.productHeader}>
                    <Text style={styles.productName}>{item.name}</Text>
                    <TouchableOpacity
                      style={styles.imageToggle}
                      onPress={() => {
                        if (!isAuthenticated) return;
                        setShowImages((prev) => ({
                          ...prev,
                          [`product1_${item.id}`]: !prev[`product1_${item.id}`],
                        }));
                      }}
                      disabled={!isAuthenticated}
                    >
                      <Text style={styles.imageToggleText}>
                        {showImage ? "▼ Hide Product Image" : "▶ Show Product Image"}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  
                  {/* Image */}
                  {showImage && catalogProduct?.imageUrl && (
                    <View style={styles.imageContainer}>
                      <Image
                        source={{ uri: catalogProduct.imageUrl }}
                        style={styles.productImage}
                        resizeMode="contain"
                      />
                    </View>
                  )}
                  
                  {/* 30/40/30 Layout */}
                  <View style={styles.productControlsContainer}>
                    {/* Left 30%: Checkbox */}
                    <View style={styles.leftContainer}>
                      <TouchableOpacity
                        style={[
                          styles.checkbox,
                          intake.returnedProduct && styles.checkboxChecked,
                          !isAuthenticated && styles.checkboxDisabled,
                        ]}
                        onPress={() => toggleReturned(item.id, "product1")}
                        disabled={!isAuthenticated}
                      >
                        {intake.returnedProduct && (
                          <Text style={styles.checkboxCheckmark}>✓</Text>
                        )}
                      </TouchableOpacity>
                      <Text style={styles.checkboxLabel}>Returned</Text>
                    </View>
                    
                    {/* Middle 40%: Penalty controls in separate container */}
                    <View style={styles.middleContainer}>
                      <View style={styles.penaltyControlsWrapper}>
                        {/* Penalty Type Checkboxes */}
                        <View style={styles.penaltyTypeContainer}>
                          <TouchableOpacity
                            style={[
                              styles.penaltyTypeCheckbox,
                              intake.penaltyType === "percentage" && styles.penaltyTypeCheckboxChecked,
                              !isAuthenticated && styles.penaltyTypeCheckboxDisabled,
                            ]}
                            onPress={() => {
                              updatePenalty(item.id, "product1", intake.penaltyValue, "percentage", item.retailPriceCents);
                            }}
                            disabled={!isAuthenticated}
                          >
                            {intake.penaltyType === "percentage" && (
                              <Text style={styles.checkboxCheckmark}>✓</Text>
                            )}
                          </TouchableOpacity>
                          <Text style={styles.penaltyTypeLabel}>%</Text>
                          
                          <TouchableOpacity
                            style={[
                              styles.penaltyTypeCheckbox,
                              intake.penaltyType === "dollar" && styles.penaltyTypeCheckboxChecked,
                              !isAuthenticated && styles.penaltyTypeCheckboxDisabled,
                            ]}
                            onPress={() => {
                              updatePenalty(item.id, "product1", intake.penaltyValue, "dollar", item.retailPriceCents);
                            }}
                            disabled={!isAuthenticated}
                          >
                            {intake.penaltyType === "dollar" && (
                              <Text style={styles.checkboxCheckmark}>✓</Text>
                            )}
                          </TouchableOpacity>
                          <Text style={styles.penaltyTypeLabel}>$</Text>
                        </View>
                        
                        {/* Slider */}
                        <View style={styles.sliderContainer}>
                        <View style={styles.sliderLabelsContainer}>
                          <Text style={styles.sliderLabel}>
                            {intake.penaltyType === "percentage" ? "0%" : formatCentsAsUSD(0)}
                          </Text>
                          <Text style={styles.sliderLabel}>
                            {intake.penaltyType === "percentage" 
                              ? "100%" 
                              : formatCentsAsUSD(item.retailPriceCents)}
                          </Text>
                        </View>
                        <input
                          type="range"
                          min="0"
                          max={intake.penaltyType === "percentage" ? 100 : item.retailPriceCents}
                          value={intake.penaltyValue}
                          onChange={(e) => {
                            if (!isAuthenticated) return;
                            const value = parseFloat(e.target.value);
                            updatePenalty(item.id, "product1", value, intake.penaltyType, item.retailPriceCents);
                          }}
                          disabled={!isAuthenticated}
                          style={styles.slider}
                        />
                        <TextInput
                          style={[
                            styles.sliderInput,
                            !isAuthenticated && styles.sliderInputDisabled,
                          ]}
                          value={
                            focusedInputs[`product1_${item.id}`]
                              ? ""
                              : intake.penaltyType === "percentage"
                              ? `${intake.penaltyValue}`
                              : `$${(intake.penaltyValue / 100).toFixed(2)}`
                          }
                          onChangeText={(text) => {
                            handlePenaltyInputChange(item.id, "product1", text, intake.penaltyType, item.retailPriceCents);
                            setFocusedInputs((prev) => ({ ...prev, [`product1_${item.id}`]: false }));
                          }}
                          onFocus={() => {
                            if (!isAuthenticated) return;
                            setFocusedInputs((prev) => ({ ...prev, [`product1_${item.id}`]: true }));
                          }}
                          onBlur={() => {
                            setFocusedInputs((prev) => ({ ...prev, [`product1_${item.id}`]: false }));
                          }}
                          keyboardType="numeric"
                          placeholder={intake.penaltyType === "percentage" ? "0" : "$0.00"}
                          placeholderTextColor="#8A9794"
                          editable={isAuthenticated}
                        />
                      </View>
                      </View>
                    </View>
                    
                    {/* Right 30%: Display amount, Reason button, Camera button */}
                    <View style={styles.rightContainer}>
                      <Text style={styles.penaltyAmount}>
                        {formatCentsAsUSD(intake.penaltyAmount)}
                      </Text>
                      <View style={styles.actionButtonsRow}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[
                            styles.iconButton,
                            !isAuthenticated && styles.iconButtonDisabled,
                          ]}
                          onPress={() => {
                            if (!isAuthenticated) return;
                            setReasonModals((prev) => ({
                              ...prev,
                              [reasonModalKey]: true,
                            }));
                          }}
                          disabled={!isAuthenticated}
                        >
                          <Text style={[
                            styles.iconButtonText,
                            !isAuthenticated && styles.iconButtonTextDisabled,
                          ]}>✏️</Text>
                        </TouchableOpacity>
                      </View>
                      <View style={styles.actionButtonsRow}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[
                            styles.iconButton,
                            !isAuthenticated && styles.iconButtonDisabled,
                          ]}
                          onPress={() => takePhoto(item.id, "product1")}
                          disabled={!isAuthenticated}
                        >
                          <Text style={[
                            styles.iconButtonText,
                            styles.cameraIconText,
                            !isAuthenticated && styles.iconButtonTextDisabled,
                          ]}>📷</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>

                  {/* Penalty Reason Display */}
                  {intake.penaltyReason && (
                    <View style={styles.reasonDisplayContainer}>
                      <View style={styles.reasonDisplayIcon}>
                        <Text style={styles.reasonDisplayIconText}>📝</Text>
                      </View>
                      <View style={styles.reasonDisplayContent}>
                        <Text style={styles.reasonDisplayLabel}>Reason:</Text>
                        <Text style={styles.reasonDisplayText}>{intake.penaltyReason}</Text>
                      </View>
                    </View>
                  )}

                  {/* Media Thumbnails List */}
                  {intake.media && intake.media.length > 0 && (
                    <View style={styles.mediaThumbnailsListContainer}>
                      <Text style={styles.mediaThumbnailsListTitle}>
                        Photos ({intake.media.length})
                      </Text>
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={styles.mediaThumbnailsListScroll}
                      >
                        <View style={styles.mediaThumbnailsList}>
                          {intake.media.map((mediaUrl, index) => {
                            const isVideo = mediaUrl.includes('.webm') || mediaUrl.includes('video');
                            return (
                              <View key={index} style={styles.cardThumbnailWrapper}>
                                <TouchableOpacity
                                  style={styles.cardThumbnail}
                                  onPress={() => setSelectedMedia({
                                    type: isVideo ? 'video' : 'photo',
                                    url: mediaUrl,
                                  })}
                                >
                                  {isVideo ? (
                                    <View style={styles.cardThumbnailVideo}>
                                      <Text style={styles.cardThumbnailVideoIcon}>📹</Text>
                                    </View>
                                  ) : (
                                    <Image
                                      source={{ uri: mediaUrl }}
                                      style={styles.cardThumbnailImage}
                                      resizeMode="cover"
                                    />
                                  )}
                                </TouchableOpacity>
                                <TouchableOpacity dataSet={{ hover: "lift" }}
                                  style={styles.cardThumbnailDeleteButton}
                                  onPress={() => handleDeleteMedia(item.id, "product1", mediaUrl)}
                                >
                                  <Text style={styles.cardThumbnailDeleteText}>✕</Text>
                                </TouchableOpacity>
                              </View>
                            );
                          })}
                        </View>
                      </ScrollView>
                    </View>
                  )}
                  
                  {/* Reason Modal */}
                  <Modal
                    visible={reasonModalVisible}
                    transparent
                    animationType="fade"
                    onRequestClose={() => {
                      setReasonModals((prev) => ({
                        ...prev,
                        [reasonModalKey]: false,
                      }));
                    }}
                  >
                    <View style={styles.modalOverlay}>
                      <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>Reason for Penalty</Text>
                        <TextInput
                          style={[
                            styles.reasonInput,
                            !isAuthenticated && styles.reasonInputDisabled,
                          ]}
                          placeholder="Enter reason..."
                          placeholderTextColor="#8A9794"
                          value={reasonTexts[reasonModalKey] || ""}
                          onChangeText={(text) => {
                            if (!isAuthenticated) return;
                            setReasonTexts((prev) => ({
                              ...prev,
                              [reasonModalKey]: text,
                            }));
                          }}
                          multiline
                          editable={isAuthenticated}
                        />
                        <View style={styles.modalButtons}>
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={[
                              styles.modalButton, 
                              styles.cancelButton,
                              !isAuthenticated && styles.buttonDisabled,
                            ]}
                            onPress={() => {
                              if (!isAuthenticated) return;
                              setReasonModals((prev) => ({
                                ...prev,
                                [reasonModalKey]: false,
                              }));
                            }}
                            disabled={!isAuthenticated}
                          >
                            <Text style={styles.modalButtonText}>Cancel</Text>
                          </TouchableOpacity>
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={[
                              styles.modalButton, 
                              styles.saveButton,
                              !isAuthenticated && styles.buttonDisabled,
                            ]}
                            onPress={() => saveReason(item.id, "product1")}
                            disabled={!isAuthenticated}
                          >
                            <Text style={styles.modalButtonText}>Save</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  </Modal>
                </View>
              );
            })}
          </View>
        )}

        {/* Product2 Items - Same structure as Product1 */}
        {product2Items.length > 0 && (
          <View style={[styles.categoryCard, styles.categoryCardYellow]}>
            {/* Badge */}
            <View style={[styles.categoryBadge, { backgroundColor: "#B4761B" }]}>
              <Text style={styles.categoryBadgeText}>
                {product2CategoryName}
              </Text>
            </View>
            {product2Items.map((item) => {
              const intake = bookingIntake.product2?.[item.id] || {
                returnedProduct: true,
                penaltyType: "percentage",
                penaltyValue: 0,
                penaltyAmount: 0,
                penaltyReason: "",
                photos: [],
                messages: [],
                media: [],
              };
              
              const showImage = showImages[`product2_${item.id}`] || false;
              const reasonModalKey = `product2_${item.id}`;
              const reasonModalVisible = reasonModals[reasonModalKey] || false;
              
              const catalogProduct = products2Catalog.find((p) => p.id === item.id);
              
              return (
                <View key={item.id} style={styles.productCard}>
                  {/* Same structure as product1 */}
                  <View style={styles.productHeader}>
                    <Text style={styles.productName}>{item.name}</Text>
                    <TouchableOpacity
                      style={styles.imageToggle}
                      onPress={() => {
                        if (!isAuthenticated) return;
                        setShowImages((prev) => ({
                          ...prev,
                          [`product2_${item.id}`]: !prev[`product2_${item.id}`],
                        }));
                      }}
                      disabled={!isAuthenticated}
                    >
                      <Text style={styles.imageToggleText}>
                        {showImage ? "▼ Hide Product Image" : "▶ Show Product Image"}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  
                  {showImage && catalogProduct?.imageUrl && (
                    <View style={styles.imageContainer}>
                      <Image
                        source={{ uri: catalogProduct.imageUrl }}
                        style={styles.productImage}
                        resizeMode="contain"
                      />
                    </View>
                  )}
                  
                  <View style={styles.productControlsContainer}>
                    <View style={styles.leftContainer}>
                      <TouchableOpacity
                        style={[
                          styles.checkbox,
                          intake.returnedProduct && styles.checkboxChecked,
                          !isAuthenticated && styles.checkboxDisabled,
                        ]}
                        onPress={() => toggleReturned(item.id, "product2")}
                        disabled={!isAuthenticated}
                      >
                        {intake.returnedProduct && (
                          <Text style={styles.checkboxCheckmark}>✓</Text>
                        )}
                      </TouchableOpacity>
                      <Text style={styles.checkboxLabel}>Returned</Text>
                    </View>
                    
                    <View style={styles.middleContainer}>
                      <View style={styles.penaltyControlsWrapper}>
                        <View style={styles.penaltyTypeContainer}>
                          <TouchableOpacity
                            style={[
                              styles.penaltyTypeCheckbox,
                              intake.penaltyType === "percentage" && styles.penaltyTypeCheckboxChecked,
                              !isAuthenticated && styles.penaltyTypeCheckboxDisabled,
                            ]}
                            onPress={() => {
                              updatePenalty(item.id, "product2", intake.penaltyValue, "percentage", item.retailPriceCents);
                            }}
                            disabled={!isAuthenticated}
                          >
                            {intake.penaltyType === "percentage" && (
                              <Text style={styles.checkboxCheckmark}>✓</Text>
                            )}
                          </TouchableOpacity>
                          <Text style={styles.penaltyTypeLabel}>%</Text>
                          
                          <TouchableOpacity
                            style={[
                              styles.penaltyTypeCheckbox,
                              intake.penaltyType === "dollar" && styles.penaltyTypeCheckboxChecked,
                              !isAuthenticated && styles.penaltyTypeCheckboxDisabled,
                            ]}
                            onPress={() => {
                              updatePenalty(item.id, "product2", intake.penaltyValue, "dollar", item.retailPriceCents);
                            }}
                            disabled={!isAuthenticated}
                          >
                            {intake.penaltyType === "dollar" && (
                              <Text style={styles.checkboxCheckmark}>✓</Text>
                            )}
                          </TouchableOpacity>
                          <Text style={styles.penaltyTypeLabel}>$</Text>
                        </View>
                        
                        <View style={styles.sliderContainer}>
                        <View style={styles.sliderLabelsContainer}>
                          <Text style={styles.sliderLabel}>
                            {intake.penaltyType === "percentage" ? "0%" : formatCentsAsUSD(0)}
                          </Text>
                          <Text style={styles.sliderLabel}>
                            {intake.penaltyType === "percentage" 
                              ? "100%" 
                              : formatCentsAsUSD(item.retailPriceCents)}
                          </Text>
                        </View>
                        <input
                          type="range"
                          min="0"
                          max={intake.penaltyType === "percentage" ? 100 : item.retailPriceCents}
                          value={intake.penaltyValue}
                          onChange={(e) => {
                            if (!isAuthenticated) return;
                            const value = parseFloat(e.target.value);
                            updatePenalty(item.id, "product2", value, intake.penaltyType, item.retailPriceCents);
                          }}
                          disabled={!isAuthenticated}
                          style={styles.slider}
                        />
                        <TextInput
                          style={[
                            styles.sliderInput,
                            !isAuthenticated && styles.sliderInputDisabled,
                          ]}
                          value={
                            focusedInputs[`product2_${item.id}`]
                              ? ""
                              : intake.penaltyType === "percentage"
                              ? `${intake.penaltyValue}`
                              : `$${(intake.penaltyValue / 100).toFixed(2)}`
                          }
                          onChangeText={(text) => {
                            handlePenaltyInputChange(item.id, "product2", text, intake.penaltyType, item.retailPriceCents);
                            setFocusedInputs((prev) => ({ ...prev, [`product2_${item.id}`]: false }));
                          }}
                          onFocus={() => {
                            if (!isAuthenticated) return;
                            setFocusedInputs((prev) => ({ ...prev, [`product2_${item.id}`]: true }));
                          }}
                          onBlur={() => {
                            setFocusedInputs((prev) => ({ ...prev, [`product2_${item.id}`]: false }));
                          }}
                          keyboardType="numeric"
                          placeholder={intake.penaltyType === "percentage" ? "0" : "$0.00"}
                          placeholderTextColor="#8A9794"
                          editable={isAuthenticated}
                        />
                      </View>
                      </View>
                    </View>
                    
                    <View style={styles.rightContainer}>
                      <Text style={styles.penaltyAmount}>
                        {formatCentsAsUSD(intake.penaltyAmount)}
                      </Text>
                      <View style={styles.actionButtonsRow}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[
                            styles.iconButton,
                            !isAuthenticated && styles.iconButtonDisabled,
                          ]}
                          onPress={() => {
                            if (!isAuthenticated) return;
                            setReasonModals((prev) => ({
                              ...prev,
                              [reasonModalKey]: true,
                            }));
                          }}
                          disabled={!isAuthenticated}
                        >
                          <Text style={styles.iconButtonText}>✏️</Text>
                        </TouchableOpacity>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[
                            styles.iconButton,
                            (!isAuthenticated || !intake.penaltyReason) && styles.iconButtonDisabled,
                          ]}
                          onPress={() => {
                            if (!isAuthenticated || !intake.penaltyReason) return;
                            setTextViewerModals((prev) => ({
                              ...prev,
                              [reasonModalKey]: true,
                            }));
                          }}
                          disabled={!isAuthenticated || !intake.penaltyReason}
                        >
                          <Text style={styles.iconButtonText}>📝</Text>
                        </TouchableOpacity>
                      </View>
                      <View style={styles.actionButtonsRow}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[
                            styles.iconButton,
                            !isAuthenticated && styles.iconButtonDisabled,
                          ]}
                          onPress={() => takePhoto(item.id, "product2")}
                          disabled={!isAuthenticated}
                        >
                          <Text style={[
                            styles.iconButtonText,
                            styles.cameraIconText,
                            !isAuthenticated && styles.iconButtonTextDisabled,
                          ]}>📷</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>

                  {/* Penalty Reason Display */}
                  {intake.penaltyReason && (
                    <View style={styles.reasonDisplayContainer}>
                      <View style={styles.reasonDisplayIcon}>
                        <Text style={styles.reasonDisplayIconText}>📝</Text>
                      </View>
                      <View style={styles.reasonDisplayContent}>
                        <Text style={styles.reasonDisplayLabel}>Reason:</Text>
                        <Text style={styles.reasonDisplayText}>{intake.penaltyReason}</Text>
                      </View>
                    </View>
                  )}

                  {/* Media Thumbnails List */}
                  {intake.media && intake.media.length > 0 && (
                    <View style={styles.mediaThumbnailsListContainer}>
                      <Text style={styles.mediaThumbnailsListTitle}>
                        Photos ({intake.media.length})
                      </Text>
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={styles.mediaThumbnailsListScroll}
                      >
                        <View style={styles.mediaThumbnailsList}>
                          {intake.media.map((mediaUrl, index) => {
                            const isVideo = mediaUrl.includes('.webm') || mediaUrl.includes('video');
                            return (
                              <View key={index} style={styles.cardThumbnailWrapper}>
                                <TouchableOpacity
                                  style={styles.cardThumbnail}
                                  onPress={() => setSelectedMedia({
                                    type: isVideo ? 'video' : 'photo',
                                    url: mediaUrl,
                                  })}
                                >
                                  {isVideo ? (
                                    <View style={styles.cardThumbnailVideo}>
                                      <Text style={styles.cardThumbnailVideoIcon}>📹</Text>
                                    </View>
                                  ) : (
                                    <Image
                                      source={{ uri: mediaUrl }}
                                      style={styles.cardThumbnailImage}
                                      resizeMode="cover"
                                    />
                                  )}
                                </TouchableOpacity>
                                <TouchableOpacity dataSet={{ hover: "lift" }}
                                  style={styles.cardThumbnailDeleteButton}
                                  onPress={() => handleDeleteMedia(item.id, "product2", mediaUrl)}
                                >
                                  <Text style={styles.cardThumbnailDeleteText}>✕</Text>
                                </TouchableOpacity>
                              </View>
                            );
                          })}
                        </View>
                      </ScrollView>
                    </View>
                  )}
                  
                  <Modal
                    visible={reasonModalVisible}
                    transparent
                    animationType="fade"
                    onRequestClose={() => {
                      setReasonModals((prev) => ({
                        ...prev,
                        [reasonModalKey]: false,
                      }));
                    }}
                  >
                    <View style={styles.modalOverlay}>
                      <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>Reason for Penalty</Text>
                        <TextInput
                          style={[
                            styles.reasonInput,
                            !isAuthenticated && styles.reasonInputDisabled,
                          ]}
                          placeholder="Enter reason..."
                          placeholderTextColor="#8A9794"
                          value={reasonTexts[reasonModalKey] || ""}
                          onChangeText={(text) => {
                            if (!isAuthenticated) return;
                            setReasonTexts((prev) => ({
                              ...prev,
                              [reasonModalKey]: text,
                            }));
                          }}
                          multiline
                          editable={isAuthenticated}
                        />
                        <View style={styles.modalButtons}>
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={[
                              styles.modalButton, 
                              styles.cancelButton,
                              !isAuthenticated && styles.buttonDisabled,
                            ]}
                            onPress={() => {
                              if (!isAuthenticated) return;
                              setReasonModals((prev) => ({
                                ...prev,
                                [reasonModalKey]: false,
                              }));
                            }}
                            disabled={!isAuthenticated}
                          >
                            <Text style={styles.modalButtonText}>Cancel</Text>
                          </TouchableOpacity>
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={[
                              styles.modalButton, 
                              styles.saveButton,
                              !isAuthenticated && styles.buttonDisabled,
                            ]}
                            onPress={() => saveReason(item.id, "product2")}
                            disabled={!isAuthenticated}
                          >
                            <Text style={styles.modalButtonText}>Save</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  </Modal>
                  
                  {/* Text Viewer Modal */}
                  <Modal
                    visible={textViewerModals[reasonModalKey] || false}
                    transparent
                    animationType="fade"
                    onRequestClose={() => {
                      setTextViewerModals((prev) => ({
                        ...prev,
                        [reasonModalKey]: false,
                      }));
                    }}
                  >
                    <View style={styles.modalOverlay}>
                      <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>Penalty Reason</Text>
                        <ScrollView style={styles.textViewerContainer}>
                          <Text style={styles.textViewerText}>
                            {intake.penaltyReason || "No reason provided"}
                          </Text>
                        </ScrollView>
                        <View style={styles.modalButtons}>
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={[styles.modalButton, styles.cancelButton]}
                            onPress={() => {
                              setTextViewerModals((prev) => ({
                                ...prev,
                                [reasonModalKey]: false,
                              }));
                            }}
                          >
                            <Text style={styles.modalButtonText}>Close</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  </Modal>

                  {/* Media Viewer Modal */}
                  <Modal
                    visible={mediaViewerModals[reasonModalKey] || false}
                    transparent
                    animationType="fade"
                    onRequestClose={() => {
                      setMediaViewerModals((prev) => ({
                        ...prev,
                        [reasonModalKey]: false,
                      }));
                      setSelectedMedia(null);
                    }}
                  >
                    <View style={styles.modalOverlay}>
                      <View style={styles.mediaViewerContent}>
                        <View style={styles.mediaViewerHeader}>
                          <Text style={styles.mediaViewerTitle}>Media ({intake.media?.length || 0})</Text>
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={styles.closeButton}
                            onPress={() => {
                              setMediaViewerModals((prev) => ({
                                ...prev,
                                [reasonModalKey]: false,
                              }));
                              setSelectedMedia(null);
                            }}
                          >
                            <Text style={styles.closeButtonText}>✕</Text>
                          </TouchableOpacity>
                        </View>
                        <ScrollView style={styles.mediaThumbnailsContainer}>
                          <View style={styles.mediaThumbnailsGrid}>
                            {intake.media?.map((mediaUrl, index) => {
                              const isVideo = mediaUrl.includes('.webm') || mediaUrl.includes('video');
                              return (
                                <View key={index} style={styles.mediaThumbnailWrapper}>
                                  <TouchableOpacity
                                    style={styles.mediaThumbnail}
                                    onPress={() => setSelectedMedia({
                                      type: isVideo ? 'video' : 'photo',
                                      url: mediaUrl,
                                    })}
                                  >
                                    {isVideo ? (
                                      <View style={styles.mediaThumbnailVideo}>
                                        <Text style={styles.mediaThumbnailVideoIcon}>📹</Text>
                                      </View>
                                    ) : (
                                      <Image
                                          source={{ uri: mediaUrl }}
                                          style={styles.mediaThumbnailImage}
                                          resizeMode="cover"
                                        />
                                    )}
                                  </TouchableOpacity>
                                  <TouchableOpacity dataSet={{ hover: "lift" }}
                                    style={styles.deleteMediaButton}
                                    onPress={() => handleDeleteMedia(item.id, "product2", mediaUrl)}
                                  >
                                    <Text style={styles.deleteMediaButtonText}>✕</Text>
                                  </TouchableOpacity>
                                </View>
                              );
                            })}
                            {(!intake.media || intake.media.length === 0) && (
                              <Text style={styles.noMediaText}>No media available</Text>
                            )}
                          </View>
                        </ScrollView>
                      </View>
                    </View>
                  </Modal>
                  
                  {/* Full-Size Media Modal */}
                  <Modal
                    visible={selectedMedia !== null}
                    transparent
                    animationType="fade"
                    onRequestClose={() => setSelectedMedia(null)}
                  >
                    <View style={styles.fullMediaOverlay}>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={styles.fullMediaCloseButton}
                        onPress={() => setSelectedMedia(null)}
                      >
                        <Text style={styles.fullMediaCloseButtonText}>✕</Text>
                      </TouchableOpacity>
                      {selectedMedia?.type === 'photo' ? (
                        <Image
                          source={{ uri: selectedMedia.url }}
                          style={styles.fullMediaImage}
                          resizeMode="contain"
                        />
                      ) : selectedMedia?.type === 'video' ? (
                        <View style={styles.fullMediaVideoContainer}>
                          <video
                            src={selectedMedia.url}
                            controls
                            style={styles.fullMediaVideo}
                            autoPlay
                          />
                        </View>
                      ) : null}
                    </View>
                  </Modal>
                </View>
              );
            })}
          </View>
        )}

        {/* Penalty Summary */}
        {penalties.total > 0 && (
          <View style={styles.penaltySummary}>
            <Text style={styles.penaltySummaryTitle}>Penalty Summary</Text>
            {penalties.items.map((item, idx) => (
              <View key={idx} style={styles.penaltyItem}>
                <Text style={styles.penaltyItemName}>{item.name}</Text>
                <Text style={styles.penaltyItemAmount}>
                  {formatCentsAsUSD(item.amount)}
                </Text>
              </View>
            ))}
            <View style={styles.penaltyTotal}>
              <Text style={styles.penaltyTotalLabel}>Total Penalties:</Text>
              <Text style={styles.penaltyTotalAmount}>
                {formatCentsAsUSD(penalties.total)}
              </Text>
            </View>
          </View>
        )}

        {/* Action Buttons */}
        {penalties.total > 0 && (
          <View style={styles.actionButtonsRow}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[
                styles.chargeButton, 
                styles.actionButtonHalf,
                !isAuthenticated && styles.buttonDisabled,
              ]}
              onPress={handleChargePenalties}
              disabled={!isAuthenticated}
            >
              <Text style={styles.chargeButtonText}>Charge Penalties</Text>
            </TouchableOpacity>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[
                  bookingIntake?.flaggedForReview ? styles.saveForReviewButtonFlagged : styles.saveForReviewButton, 
                styles.actionButtonHalf,
                (saveForReviewProcessing || !isAuthenticated) && styles.saveForReviewButtonDisabled
              ]}
              onPress={handleSaveForReview}
              disabled={saveForReviewProcessing || !isAuthenticated}
            >
              <Text style={styles.saveForReviewButtonText}>
                  {bookingIntake?.flaggedForReview
                    ? "Flagged"
                    : (saveForReviewProcessing ? "Flagging..." : "Flag for Review")}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Complete Booking Button - Show when no penalties */}
        {penalties.total === 0 && (
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              styles.completeBookingButton,
              !isAuthenticated && styles.buttonDisabled,
            ]}
            onPress={() => {
              if (!isAuthenticated) return;
              setCompleteBookingModalVisible(true);
            }}
            disabled={!isAuthenticated}
          >
            <Text style={styles.completeBookingButtonText}>
              ✅ Complete Booking (No Penalties)
            </Text>
          </TouchableOpacity>
        )}

        {/* Complete Booking Modal */}
        <Modal
          visible={completeBookingModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => {
            if (!completeBookingProcessing) {
              setCompleteBookingModalVisible(false);
            }
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.chargeModalContent}>
              {!completeBookingProcessing ? (
                <>
                  <Text style={styles.chargeModalTitle}>Complete Booking</Text>
                  <Text style={styles.chargeModalAmount}>
                    No Penalties - All Items Returned in Good Condition
                  </Text>
                  
                  {getUncheckedProducts().length > 0 && (
                    <View style={styles.uncheckedProductsContainer}>
                      <Text style={styles.uncheckedProductsTitle}>
                        Items Not Checked:
                      </Text>
                      {getUncheckedProducts().map((item, idx) => (
                        <Text key={idx} style={styles.uncheckedProductItem}>
                          • {item.name}
                        </Text>
                      ))}
                    </View>
                  )}
                  
                  <View style={styles.chargeModalButtons}>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[styles.modalButton, styles.cancelButton]}
                      onPress={() => {
                        setCompleteBookingModalVisible(false);
                      }}
                    >
                      <Text style={styles.modalButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[styles.modalButton, styles.confirmButton]}
                      onPress={() => {
                        if (!isAuthenticated) return;
                        handleCompleteBooking();
                      }}
                      disabled={!isAuthenticated}
                    >
                      <Text style={styles.modalButtonText}>Confirm</Text>
                    </TouchableOpacity>
                  </View>
                </>
              ) : (
                <View style={styles.processingContainer}>
                  <ActivityIndicator size="large" color="#0E7C6B" />
                  <Text style={styles.processingText}>Completing booking...</Text>
                  <Text style={styles.processingSubtext}>
                    Saving check-in data, moving to completed bookings, and sending notifications...
                  </Text>
                </View>
              )}
            </View>
          </View>
        </Modal>

        {/* Review Request Ask Modal */}
        <Modal
          visible={reviewAskVisible}
          transparent
          animationType="fade"
          onRequestClose={() => {
            if (!reviewAskBusy) {
              handleReviewAskDecline();
            }
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.chargeModalContent}>
              <Text style={styles.chargeModalTitle}>Guaranteed 5-Star Transaction?</Text>
              <Text style={styles.chargeModalAmount}>
                Text {reviewRequestHasEmail ? "and email " : ""}{booking?.firstName || "the customer"} a review link? It will be sent in about {reviewRequestDelayHours} hours.
              </Text>

              <View style={styles.chargeModalButtons}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.modalButton, styles.cancelButton]}
                  onPress={handleReviewAskDecline}
                  disabled={reviewAskBusy}
                >
                  <Text style={styles.modalButtonText}>No Thanks</Text>
                </TouchableOpacity>

                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.modalButton, styles.confirmButton]}
                  onPress={handleReviewAskAccept}
                  disabled={reviewAskBusy}
                >
                  {reviewAskBusy ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.modalButtonText}>Send Review Link</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Charge Penalties Modal */}
        <Modal
          visible={chargeModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => {
            if (!chargeProcessing && !sendingNotifications) {
              setChargeModalVisible(false);
              setChargeSliderPosition(0);
            }
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.chargeModalContent}>
              {!chargeProcessing && !chargeSuccess ? (
                <>
                  <Text style={styles.chargeModalTitle}>Confirm Charge</Text>
                  <Text style={styles.chargeModalAmount}>
                    Total: {formatCentsAsUSD(penalties.total)}
                  </Text>
                  
                  {getUncheckedProducts().length > 0 && (
                    <View style={styles.uncheckedProductsContainer}>
                      <Text style={styles.uncheckedProductsTitle}>
                        Items Not Checked:
                      </Text>
                      {getUncheckedProducts().map((item, idx) => (
                        <Text key={idx} style={styles.uncheckedProductItem}>
                          • {item.name}
                        </Text>
                      ))}
                    </View>
                  )}
                  
                  <View style={styles.chargeModalButtons}>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[styles.modalButton, styles.cancelButton]}
                      onPress={() => {
                        setChargeModalVisible(false);
                        setChargeSliderPosition(0);
                      }}
                    >
                      <Text style={styles.modalButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    
                    <View style={styles.sliderContainer}>
                      <Text style={styles.sliderText}>Slide to Confirm</Text>
                      <View
                        style={[
                          styles.sliderTrack,
                          !isAuthenticated && styles.sliderTrackDisabled,
                        ]}
                        ref={chargeSliderTrackRef}
                        onTouchStart={(e) => {
                          if (!isAuthenticated) return;
                          const track = chargeSliderTrackRef.current;
                          if (!track) return;
                          const rect = track.getBoundingClientRect();
                          const x = (e.touches?.[0]?.clientX || e.nativeEvent?.touches?.[0]?.clientX || 0) - rect.left;
                          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                          setChargeSliderPosition(percentage);
                        }}
                        onTouchMove={(e) => {
                          if (!isAuthenticated) return;
                          const track = chargeSliderTrackRef.current;
                          if (!track) return;
                          const rect = track.getBoundingClientRect();
                          const x = (e.touches?.[0]?.clientX || e.nativeEvent?.touches?.[0]?.clientX || 0) - rect.left;
                          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                          setChargeSliderPosition(percentage);
                        }}
                        onTouchEnd={() => {
                          if (!isAuthenticated) return;
                          if (chargeSliderPosition >= 100) {
                            handleChargeConfirm();
                          } else {
                            setChargeSliderPosition(0);
                          }
                        }}
                        onMouseDown={(e) => {
                          if (!isAuthenticated) return;
                          const track = chargeSliderTrackRef.current;
                          if (!track) return;
                          const rect = track.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                          setChargeSliderPosition(percentage);
                          
                          const handleMouseMove = (moveEvent) => {
                            if (!isAuthenticated) return;
                            const moveRect = track.getBoundingClientRect();
                            const moveX = moveEvent.clientX - moveRect.left;
                            const movePercentage = Math.max(0, Math.min(100, (moveX / moveRect.width) * 100));
                            setChargeSliderPosition(movePercentage);
                          };
                          
                          const handleMouseUp = () => {
                            if (!isAuthenticated) return;
                            if (chargeSliderPosition >= 100) {
                              handleChargeConfirm();
                            } else {
                              setChargeSliderPosition(0);
                            }
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };
                          
                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                      >
                        <View
                          style={[
                            styles.sliderFill,
                            { width: `${chargeSliderPosition}%` },
                          ]}
                        />
                        <View
                          style={[
                            styles.sliderThumb,
                            { left: `${chargeSliderPosition}%` },
                          ]}
                        >
                          <Text style={styles.sliderThumbText}>
                            {chargeSliderPosition >= 100 ? "✓" : "→"}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>
                </>
              ) : chargeProcessing ? (
                <View style={styles.processingContainer}>
                  <ActivityIndicator size="large" color="#0E7C6B" />
                  <Text style={styles.processingText}>Processing charge...</Text>
                </View>
              ) : chargeSuccess ? (
                <View style={styles.successContainer}>
                  <Text style={styles.successText}>✓ Charge Successful!</Text>
                </View>
              ) : sendingNotifications ? (
                <View style={styles.processingContainer}>
                  <ActivityIndicator size="large" color="#1F8A5B" />
                  <Text style={styles.processingText}>Sending notifications...</Text>
                  <Text style={styles.processingSubtext}>Emailing and texting customer with penalty details and photos</Text>
                </View>
              ) : null}
            </View>
          </View>
        </Modal>
      </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
  errorText: {
    fontSize: 18,
    color: "#C4453D",
    textAlign: "center",
    marginTop: 50,
  },
  scrollView: {
    flex: 1,
  },
  actionButtonsContainer: {
    flexDirection: "row",
    padding: 16,
    gap: 12,
  },
  actionButton: {
    flex: 1,
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
  },
  returnAllButton: {
    backgroundColor: "#1F8A5B",
  },
  clearAllButton: {
    backgroundColor: "#C4453D",
  },
  actionButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  categoryCard: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    borderRadius: 6,
    borderWidth: 2,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  categoryCardGreen: {
    borderColor: "#1F8A5B",
  },
  categoryCardYellow: {
    borderColor: "#B4761B",
  },
  categoryCardBlue: {
    borderColor: "#0E7C6B",
  },
  categoryBadge: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    alignItems: "center",
  },
  categoryBadgeText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  productCard: {
    paddingBottom: 16,
    marginBottom: 16,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  productHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  productName: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2E3A38",
    flex: 1,
  },
  imageToggle: {
    padding: 8,
  },
  imageToggleText: {
    fontSize: 14,
    color: "#0E7C6B",
  },
  imageContainer: {
    width: "100%",
    marginBottom: 12,
    alignItems: "center",
  },
  productImage: {
    width: "100%",
    maxWidth: 300,
    aspectRatio: 1,
    borderRadius: 8,
  },
  productControlsContainer: {
    flexDirection: "row",
    width: "100%",
  },
  reasonDisplayContainer: {
    flexDirection: "row",
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    padding: 12,
    marginTop: 16,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
    alignItems: "flex-start",
  },
  reasonDisplayIcon: {
    marginRight: 12,
    marginTop: 2,
  },
  reasonDisplayIconText: {
    fontSize: 20,
  },
  reasonDisplayContent: {
    flex: 1,
  },
  reasonDisplayLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  reasonDisplayText: {
    fontSize: 14,
    color: "#2E3A38",
    lineHeight: 20,
  },
  leftContainer: {
    width: "30%",
    alignItems: "center",
    justifyContent: "center",
  },
  middleContainer: {
    width: "40%",
    paddingHorizontal: 8,
  },
  rightContainer: {
    width: "30%",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  checkbox: {
    width: 28,
    height: 28,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 8,
  },
  checkboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkboxCheckmark: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  checkboxLabel: {
    fontSize: 14,
    color: "#2E3A38",
  },
  sendToCustomerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
    gap: 8,
  },
  penaltyControlsWrapper: {
    backgroundColor: "#F4F6F6",
    padding: 8,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  penaltyTypeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    gap: 8,
  },
  penaltyTypeCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
  },
  penaltyTypeCheckboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  penaltyTypeLabel: {
    fontSize: 14,
    color: "#2E3A38",
    marginRight: 12,
  },
  sliderContainer: {
    marginTop: 8,
  },
  sliderLabelsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  sliderLabel: {
    fontSize: 10,
    color: "#5C6B68",
  },
  slider: {
    width: "70%",
    height: 8,
    alignSelf: "center",
  },
  sliderInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 4,
    padding: 6,
    fontSize: 12,
    textAlign: "center",
    marginTop: 4,
    backgroundColor: "#FFFFFF",
    width: 50,
    alignSelf: "center",
  },
  sliderValue: {
    fontSize: 12,
    color: "#5C6B68",
    textAlign: "center",
    marginTop: 4,
  },
  penaltyAmount: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#C4453D",
    marginBottom: 8,
  },
  reasonButton: {
    backgroundColor: "#0E7C6B",
    padding: 8,
    borderRadius: 5,
    marginBottom: 8,
    minWidth: 80,
    alignItems: "center",
  },
  reasonButtonText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
  },
  cameraButton: {
    backgroundColor: "#1F8A5B",
    padding: 8,
    borderRadius: 5,
    minWidth: 80,
    alignItems: "center",
  },
  cameraButtonText: {
    fontSize: 20,
  },
  photoCount: {
    fontSize: 11,
    color: "#5C6B68",
    marginTop: 4,
  },
  penaltySummary: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  penaltySummaryTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#2E3A38",
  },
  penaltyItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  penaltyItemName: {
    fontSize: 14,
    color: "#2E3A38",
  },
  penaltyItemAmount: {
    fontSize: 14,
    color: "#C4453D",
    fontWeight: "600",
  },
  penaltyTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 2,
    borderTopColor: "#C4453D",
  },
  penaltyTotalLabel: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  penaltyTotalAmount: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#C4453D",
  },
  actionButtonsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  iconButton: {
    padding: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  iconButtonDisabled: {
    opacity: 0.4,
  },
  iconButtonText: {
    fontSize: 18,
    textAlign: "center",
  },
  cameraIconText: {
    fontSize: 23,
  },
  iconButtonTextDisabled: {
    opacity: 0.4,
    color: "#8A9794",
  },
  mediaCountButton: {
    padding: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  mediaCountButtonDisabled: {
    opacity: 0.4,
  },
  mediaCountText: {
    fontSize: 14,
    color: "#2E3A38",
    fontWeight: "500",
  },
  mediaCountTextDisabled: {
    color: "#8A9794",
    opacity: 0.4,
  },
  actionButtonHalf: {
    flex: 1,
  },
  topButtonsContainer: {
    flexDirection: "row",
    gap: 10,
    margin: 16,
    marginBottom: 8,
  },
  autoSaveIndicator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#E4F4EC",
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 5,
    marginHorizontal: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#1F8A5B",
  },
  autoSaveIcon: {
    fontSize: 16,
    marginRight: 8,
  },
  autoSaveText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#1F8A5B",
  },
  topButton: {
    flex: 1,
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  viewBookingButton: {
    backgroundColor: "#0E7C6B",
  },
  photosVideosButtonTop: {
    backgroundColor: "#B4761B",
  },
  topButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  photosVideosButton: {
    backgroundColor: "#B4761B",
    padding: 16,
    borderRadius: 6,
    margin: 16,
    marginBottom: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  photosVideosButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  chargeButton: {
    backgroundColor: "#C4453D",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
  },
  chargeButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  saveForReviewButton: {
    backgroundColor: "#0E7C6B",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
  },
  saveForReviewButtonFlagged: {
    backgroundColor: "#B4761B",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
  },
  saveForReviewButtonDisabled: {
    backgroundColor: "#8A9794",
  },
  saveForReviewButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  buttonDisabled: {
    opacity: 0.5,
    backgroundColor: "#DDE3E2",
  },
  checkboxDisabled: {
    opacity: 0.5,
    backgroundColor: "#EDF1F0",
  },
  penaltyTypeCheckboxDisabled: {
    opacity: 0.5,
    backgroundColor: "#EDF1F0",
  },
  sliderInputDisabled: {
    opacity: 0.5,
    backgroundColor: "#EDF1F0",
  },
  reasonInputDisabled: {
    opacity: 0.5,
    backgroundColor: "#EDF1F0",
  },
  sliderTrackDisabled: {
    opacity: 0.5,
  },
  completeBookingButton: {
    backgroundColor: "#1F8A5B",
    padding: 16,
    borderRadius: 6,
    margin: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  completeBookingButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  completeBookingContainer: {
    margin: 16,
    padding: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
  },
  completeBookingText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 12,
    textAlign: "center",
  },
  sliderTrack: {
    height: 50,
    backgroundColor: "#DDE3E2",
    borderRadius: 25,
    position: "relative",
    overflow: "hidden",
    width: "100%",
    cursor: "pointer",
  },
  sliderFill: {
    height: "100%",
    backgroundColor: "#1F8A5B",
    borderRadius: 25,
    position: "absolute",
    left: 0,
    top: 0,
  },
  sliderThumb: {
    position: "absolute",
    width: 50,
    height: 50,
    backgroundColor: "#FFFFFF",
    borderRadius: 25,
    borderWidth: 2,
    borderColor: "#1F8A5B",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    zIndex: 10,
    cursor: "pointer",
  },
  sliderThumbText: {
    fontSize: 20,
    color: "#1F8A5B",
    fontWeight: "bold",
  },
  sliderText: {
    marginTop: 8,
    textAlign: "center",
    fontSize: 14,
    color: "#5C6B68",
    fontWeight: "500",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "85%",
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#2E3A38",
  },
  reasonInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    textAlignVertical: "top",
    marginBottom: 16,
  },
  modalButtons: {
    flexDirection: "row",
    gap: 12,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 6,
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#8A9794",
  },
  saveButton: {
    backgroundColor: "#0E7C6B",
  },
  confirmButton: {
    backgroundColor: "#1F8A5B",
  },
  modalButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  mediaViewerContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    width: "90%",
    maxWidth: 600,
    maxHeight: "80vh",
  },
  mediaViewerHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  mediaViewerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#EDF1F0",
    justifyContent: "center",
    alignItems: "center",
  },
  closeButtonText: {
    fontSize: 20,
    color: "#2E3A38",
    fontWeight: "bold",
  },
  mediaThumbnailsContainer: {
    maxHeight: "60vh",
  },
  mediaThumbnailsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  mediaThumbnailsListContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
  },
  mediaThumbnailsListTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 12,
  },
  mediaThumbnailsListScroll: {
    width: "100%",
  },
  mediaThumbnailsList: {
    flexDirection: "row",
    gap: 12,
    paddingBottom: 8,
  },
  cardThumbnailWrapper: {
    position: "relative",
    width: 80,
    height: 80,
  },
  cardThumbnail: {
    width: 80,
    height: 80,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#EDF1F0",
  },
  cardThumbnailImage: {
    width: "100%",
    height: "100%",
  },
  cardThumbnailVideo: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#DDE3E2",
  },
  cardThumbnailVideoIcon: {
    fontSize: 32,
  },
  cardThumbnailDeleteButton: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "rgba(255, 59, 48, 0.9)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  cardThumbnailDeleteText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
    lineHeight: 12,
  },
  mediaThumbnailWrapper: {
    position: "relative",
    width: 100,
    height: 100,
  },
  mediaThumbnail: {
    width: 100,
    height: 100,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#EDF1F0",
  },
  mediaThumbnailImage: {
    width: "100%",
    height: "100%",
  },
  deleteMediaButton: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "rgba(255, 59, 48, 0.9)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  deleteMediaButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
    lineHeight: 14,
  },
  mediaThumbnailVideo: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#DDE3E2",
  },
  mediaThumbnailVideoIcon: {
    fontSize: 40,
  },
  textViewerContainer: {
    maxHeight: 300,
    marginBottom: 16,
  },
  textViewerText: {
    fontSize: 16,
    color: "#2E3A38",
    lineHeight: 24,
    padding: 12,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
  },
  noMediaText: {
    fontSize: 16,
    color: "#8A9794",
    textAlign: "center",
    padding: 20,
    fontStyle: "italic",
  },
  fullMediaOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    justifyContent: "center",
    alignItems: "center",
  },
  fullMediaCloseButton: {
    position: "absolute",
    top: 20,
    right: 20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  fullMediaCloseButtonText: {
    fontSize: 24,
    color: "#FFFFFF",
    fontWeight: "bold",
  },
  fullMediaImage: {
    width: "90%",
    maxWidth: 1200,
    height: "80vh",
    maxHeight: 800,
  },
  fullMediaVideoContainer: {
    width: "90%",
    maxWidth: 1200,
    height: "80vh",
    maxHeight: 800,
  },
  fullMediaVideo: {
    width: "100%",
    height: "100%",
  },
  chargeModalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "85%",
    maxWidth: 500,
  },
  chargeModalTitle: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#2E3A38",
    textAlign: "center",
  },
  chargeModalAmount: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#C4453D",
    textAlign: "center",
    marginBottom: 24,
  },
  uncheckedProductsContainer: {
    backgroundColor: "#FDF3E3",
    padding: 12,
    borderRadius: 6,
    marginBottom: 24,
  },
  uncheckedProductsTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#7A4E12",
    marginBottom: 8,
  },
  uncheckedProductItem: {
    fontSize: 13,
    color: "#7A4E12",
    marginBottom: 4,
  },
  chargeModalButtons: {
    gap: 12,
  },
  processingContainer: {
    alignItems: "center",
    padding: 24,
  },
  processingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
  processingSubtext: {
    marginTop: 8,
    fontSize: 13,
    color: "#8A9794",
    textAlign: "center",
    lineHeight: 18,
  },
  successContainer: {
    alignItems: "center",
    padding: 24,
  },
  successText: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#1F8A5B",
  },
});

export default CheckIn;


